/* ==========================================================================
   BRITTEN CUSTOM: Header v2 -- behaviour (one small vanilla module)
   ==========================================================================

   Added:       September 2026
   Author:      Claude / Matthew
   Location:    assets/britten-header.js
   Loaded by:   sections/britten-header.liquid (<script defer>, at the end of
                the header markup, so it runs after the theme's own deferred
                scripts and before DOMContentLoaded)

   PURPOSE
   Everything the new header does that CSS cannot: hover-intent mega panels,
   the compact bar on scroll, the announcement rotation, the full-screen
   mobile menu and search, and predictive search. No jQuery.

   HOW IT WORKS
   - Markup is server-rendered (sections/britten-header.liquid + snippets/britten-header-*.liquid); the script
     only toggles state. State lives in classes on the .bh root (bh--compact,
     bh--shown, bh--open, bh--search, bh--menu), bh-item--open on the open
     menu item, bh-nav--section on the nav while a mobile section screen is
     showing, and html.bh-lock while a full-screen mobile layer blocks the page.
   - Every customer-visible string reaches the script as a data-bh-* attribute
     on the .bh root (translated server-side), so this file holds no English.
   - Desktop panels: open after 120ms of hover, instantly when another panel
     is already open, close 200ms after the pointer leaves header + panel.
     Keyboard focus on a top link opens its panel; Esc closes it and returns
     focus. On touch at 1024px and up, the first tap on a link that has a
     panel opens the panel, the second tap follows the link.
   - Compact bar (desktop only): the SAME <header> switches to position:fixed.
     Shows once the page has scrolled 40px past the editorial header, returns
     to the editorial header at 20px before its bottom edge, hides when
     scrolling down past 400px, never hides while a panel or the search is
     open. Reads scrollY inside a requestAnimationFrame, nothing else.
   - --bh-offset on <html> is the height of the fixed header currently covering
     the top of the viewport: 65px while the compact bar is showing, else 0.
     Sticky things that must sit below the header read it with a fallback:
     top: var(--bh-offset, 0px).
   - Mobile (below 1024px): the hamburger opens the nav as a full-screen layer.
     Tapping a top link opens its section screen instead of navigating.
     Focus moves into the layer and back; Tab is kept inside it; Esc closes.
   - New mobile menu (12 Oct 2026, bh--m2 on the root, settings.britten_mobile_menu_v2): the nav is the same
     full-screen layer but holds an accordion (snippets/britten-header-mobile-menu.liquid) instead of the section
     screens. Sections (data-bh-ms-btn) open one at a time and a tap on the open one shuts it; groups
     (data-bh-toggle) open on their own. The panel header has its own search button (a [data-bh-search-open] like the
     bar's), its own Saved bookmark (a [data-bh-saved-toggle]) and a Bag link (data-bh-menu-bag) that closes the menu and
     clicks the bar's real bag so the bag drawer opens. "Ask the workshop" (data-bh-ws) gets its open / closed line
     when the menu opens (updateWorkshop, the footer card's hours, 09:00 to 16:00 Europe/London, Monday to Friday, not on a
     data-bf-closed date). Back from the Saved screen returns focus to the control she used. With the setting off none of
     this markup exists and every line of it is a no-op.
   - Phone modal + Back (13 Oct 2026): while the drawer is open the nav is a role=dialog aria-modal layer named by
     the hamburger's label, the list inside it keeps a navigation landmark, and everything else on the page is inert
     (makeModal / unmakeModal, which put back exactly what they changed). Opening pushes ONE history entry
     ({bhMenu:true}); Back, X and Esc all close through popstate, so Back closes the drawer instead of leaving the page.
   - Phone bar (13 Oct 2026, BH_MOBILE_STICKY): below 1024px, once the page is scrolled 80px past the header, the 56px bar
     (not the announcement) goes position:fixed (bh--mstuck), hides on scroll down and shows on scroll up (bh--mshown),
     8px hysteresis. The spacer already holds its 56px, so nothing moves. --bh-mobile-bar on <html> is 56px while it
     shows, else 0px. Hidden while the drawer or search is open.
   - Predictive search: Shopify's /search/suggest.json, debounced 200ms, the
     previous request aborted, results built with textContent (never innerHTML).
     13 Oct 2026: search is the theme's own, on every device (the old third-party search app is cancelled and
     all hand-off code is gone). The search buttons always open our layer; from the drawer, the
     drawer closes first. Before a query is sent a small synonym map rewrites it (swatch -> samples,
     hanger -> hanging hook, isabella -> its product); the text in the box never changes.
   - Search logic (13 Oct 2026, search redesign v2, stage 1): window.BHSearch, one block with no DOM access. Always
     asks Shopify for 10 products, then BHSearch.buildModel(response, typed, ctx) returns what to show: the lead
     (a product whose name is the query), at most 6 products (veil-first reorder, name / style parsed from the
     title), 2 shop rows (matching drawer links, then Shopify collections), help rows (service questions: up to 3;
     beside a product word: exactly 1; "order" / "buy" before a product word is a verb and gets none), 2 guides,
     See all, the no-match flag and counts for a screen-reader status line. A service-only query ("returns",
     "my order", "contact", "help" alone) hides the products. Nothing is ever filtered against the typed text.
     The ctx is built once at init from the drawer (menu model, product words, help rows found by href) and the
     locale's intent words (data-bh-w-*); English words are fixed in code and always on. renderResults only draws
     the model (stage 2, below); with a lead the model carries no Shopify-page guides.
   - Search sheet (13 Oct 2026, stage 2): ONE element for every width, [data-bh-search]. Below 1024px it is the
     full-screen layer (top band: X, magnifier, input, Clear; then ONE scroller holding everything) and gets
     role=dialog, aria-modal and aria-label (syncSearchRole; desktop search is not modal, so none there); it always covers
     the whole layout viewport, and fitViewport turns the keyboard into padding on the scroller (--bh-kb). From 1024px it
     is the sheet under the header (input row sticks, the sheet scrolls). The empty state (Popular, Start here, Your Britten on phones, Bestsellers) is
     Liquid inside [data-bh-empty]; the results are drawn into [data-bh-results]; exactly one of the two shows.
   - Desktop: ONE input, in the sheet. The bar holds a Search button (a link to /search with role=button, the magnifier
     and the word, [data-bh-search-btn]; the same element in the editorial and the compact bar, no field). Click, Enter or
     Space opens the sheet with focus in its input and the caret at the end of any kept text; Search again closes it. Esc,
     Close, Search again, a click outside, a link click or focus leaving the header closes it and the input keeps its text
     (Esc is taken so the browser does not empty the search field); Esc, Close and Search again return focus to the Search
     button. Reopening shows the kept text's results at once from the last answer (cache, keyed by the query SENT) or
     asks. Clear (only with text) empties the input, shows the empty state and keeps focus. Enter submits the sheet's
     form to the search page with the aliased query (BHSearch.expandQuery) and type=product; an empty Enter is blocked and
     refocuses. The sheet stays open when the page scrolls into the compact bar and hangs from it. Phones clear the text
     on close, as before. Opening runs updateWorkshop(), so the Contact help row's dot is current.
   - On the /search page the sheet never opens: the Search button, the phone magnifier and the drawer's search entries
     close the drawer if it is open, move focus to the page's own input ([data-bh-page-input]), select its text and
     scroll to the top. A page with no such input (the old results page) gets the sheet, as everywhere else.
   - Arrow keys: Down from the input walks an explicit list, never the DOM: results = lead, products, shop rows,
     help rows, guides, See all (the left column comes first on desktop, so DOM order would be wrong); empty state =
     Popular, then Start here. A hidden element is never focused and the key is only taken when focus really moved.
   - Analytics (bhTrack): search_open { source: button | menu, handler } (button = the Search button or the phone
     magnifier, was bar; a phone search opened from the drawer keeps source menu); search_click { kind: lead | product | shop | help | guide | see_all, position },
     position is 1-based inside its kind, one event per click on a result link (see_all is the "See all results"
     link). No query text is sent.
   - Saved ("Your Britten", S1, only when the flags render it): the bookmark before the bag
     opens a panel under the header (desktop, click, bh--saved + bh--open, the page dims) or,
     below 1024px, the menu layer straight on its account screen (a second level of the nav:
     bh-nav--saved). State: savedOpen. Opening it closes the picker, the search and any mega
     panel; Esc closes it first; a click outside, or focus leaving the header, closes it; focus
     moves into it and back to the toggle. On mobile "closing" it goes back to the menu list;
     the menu's X closes both. The CONTENT (thumbnails, counts, preferences, sign out) is
     assets/britten-yb-panel.js; this file only opens, closes and moves focus. No Saved markup
     = every line of it is a no-op. saved_open is emitted through BrittenCore when it is there.
   - Country / currency / language picker: a UI shell over the store's ONE localization form.
     Options are copied from #FooterCountryForm on open. The panel reads "You are browsing the
     {country} site", then the country select, then "Change language": a language select when
     the chosen country offers more than one language (assets/britten-markets.json; a country
     missing from it has one, English), else a sentence naming the single language. It updates
     as the country select changes. Nothing is sent until Apply: that sets country_code on THAT
     form (stale currency_code / locale_code removed, return_to = this page), plus locale_code
     only when a language select is showing, and submits it. Apply with nothing changed just
     closes. The trigger sits at the left of the editorial header (and in the compact bar, only
     when settings.britten_header_compact_currency is on and from 1100px up); the popover opens under it, with a small arrow at the pill. On mobile the same
     content is the bottom sheet in the menu. window.BrittenHeaderLoc.open(code) lets the footer,
     shipping bar, delivery estimate and cart delivery open the same panel on desktop (see
     snippets/britten-location-preferences.liquid); below 1024px they keep the old bottom sheet.
     window.BrittenHeaderLoc.apply(code, locale) submits the same form the panel's Apply does
     (the first-visit language suggestion uses it); it opens nothing.
     The header holds no form and no country_code field of its own.
   - Mega panel veil: rgba .12, added 250ms after a panel opens (bh--dim); the search
     sheet dims at the same .12 but at once (a click, not a hover).
   - Panel images stay lazy; a panel's images switch to eager on hover or focus
     intent, before the open delay. A #hash link on the same page closes the
     mobile layers; the pageshow event (bfcache) resets everything.
   - The old Flex scripts read Shopify.theme.jsHeader, which only exists when
     the old header section renders. With this header on it does not, so a
     small inert stub is installed (installLegacyStub) and nothing throws.

   PUBLIC SURFACE
   - window._brittenHeaderInit()          re-run the (idempotent) setup
   - window.brittenHeaderStickyHeight()   65 on desktop, 0 on mobile
   - window.BHSearch                      the search logic (expandQuery, buildModel, readMenu, parseTitle, statusLine ...);
                                          scripts/header-search-test.js runs it against recorded Shopify responses

   THEME DEPENDENCY
   - sections/britten-header.liquid + snippets/britten-header-*.liquid markup (data-bh-* hooks, including
     snippets/britten-header-mobile-menu.liquid: data-bh-ms-btn, data-bh-ws, data-bh-ws-*, data-bh-menu-bag)
   - the footer card's data-bf-closed (sections/britten-footer.liquid), read for the workshop's closed dates
   - assets/britten-header.css (state classes)
   - the bag hooks the cart scripts use (data-bind="itemCount",
     data-ajax-cart-trigger, .has-cart-count) are markup only; this file does
     not touch them

   CHANGELOG
   - Sep 2026: Initial version (header v2, step 1 of the handoff).
   - Sep 2026: fix pass. Pills and View all go to the search page; limit_scope=each; panel images
     go eager on intent; hash links / panel links close layers; pageshow reset;
     announcement wrap-around slides on to a clone instead of back through
     every message.
   - 30 Sep 2026: the picker moved from the announcement bar to the left of the
     editorial header; its popover may stay open while the compact bar shows (the
     trigger is in the compact bar from 1100px up).
   - 30 Sep 2026: the picker applies on change (no Apply button); keyboard-driven changes
     wait for Enter (WCAG 3.2.2); a busy note shows while the page reloads.
   - 4 Oct 2026: the picker has a language row and an Apply button on every market; a country
     change no longer submits by itself (so no Enter hint). Other country controls open this
     panel instead of the old Location preferences popover (desktop, header v2 on).
   - 30 Sep 2026: the search panel lines up with the editorial input (--bh-search-x);
     results are up to 4 products with 56px thumbnails on desktop, then text links,
     then "See all results for '{{ q }}'".
   - 30 Sep 2026: the header is a section (Popular searches are blocks): chips without a
     link carry data-bh-q and go to the search page, chips with a link
     are plain links; init() re-runs on shopify:section:load.
   - 30 Sep 2026: the picker popover is clamped inside the viewport (16px gutter) and the
     veil's 250ms timer is cancelled the moment the pointer leaves the header.
   - 30 Sep 2026: the mega panel overlay waits 250ms (bh--dim) and is lighter (CSS).
   - 30 Sep 2026: desktop search is a real input in the editorial header (see
     above); the panel's input is only used by the compact bar and the code paths
     that call showSearch().
   - 30 Sep 2026: country / currency picker (step 4) driving the footer's
     localization form; the breakpoint handler ignores a "change" event that
     changed nothing.
   - 30 Sep 2026: Your Britten (S1): the Saved panel / mobile account screen state
     (savedOpen, openSaved, closeSaved, bh--saved, bh-nav--saved).
   - 5 Oct 2026: window.BrittenHeaderLoc.apply(code, locale) for the language suggestion.
   - 2 Oct 2026: the announcement bar's "Hello, name" button is a saved toggle that sits outside
     <header>; the outside-click handler now treats every saved toggle as inside, or the click
     that opened the panel closed it again.
   - 5 Oct 2026: the compact bar shrinks and removes instead of rearranging (no logo, search icon
     and the optional currency trigger at the left, nav centred where it was, Saved + bag at the
     right; see the CSS). The picker trigger is in the compact bar only when
     settings.britten_header_compact_currency puts bh--cur on the root, and then only from
     1100px up; compactHasLoc() says so, so a popover open from the editorial header closes when
     the compact bar takes over and has no trigger of its own.
   - 5 Oct 2026: the Saved panel's open focus (desktop): Close is focused only when the panel was opened from the
     keyboard; a mouse or touch open focuses the panel itself, so no focus box shows. Mobile focus is unchanged.
   - 12 Oct 2026: new mobile menu (bh--m2). One-open-at-a-time section accordion, the panel's Bag link (closes the menu,
     presses the bar's bag), the workshop open / closed line (updateWorkshop, run when the menu opens), and closeSaved
     returns focus to the control used (the panel's bookmark or the Your Britten row). The existing search-opener and
     saved-toggle wiring already binds every match, so the new search and bookmark buttons need nothing else.
   - 12 Oct 2026: the mobile Tab focus trap steps focus by hand (always preventDefault, next / previous in focusables(layer),
     wrapping at both ends) instead of only at the first / last element. Safari does not Tab to links or buttons, so the
     browser used to jump from Close to a form field behind the menu and focus escaped nav#bh-nav.
   - 13 Oct 2026: menu fixes. Modal semantics + inert page behind the drawer, Back closes the drawer (one history entry),
     the compact sticky bar on phones, bhTrack events (menu_open, menu_close, menu_section, menu_link, search_open),
     and the search button's aria-expanded.
   - 13 Oct 2026: search is the theme's own since today (old third-party search app cancelled): its detection, hand-off, opener class,
     exclude-input script and the britten_search_native_mobile setting are removed. Synonyms and Help rows added.
   - 13 Oct 2026: search redesign v2, stage 1 (the data and logic layer; the layout was stage 2). New window.BHSearch block:
     fold (case, accents, spaces), aliases (sent AND used for the lead), name parse, lead match (exact, or one typo from 5
     characters), intent (service / mixed / guide / verb / plain; English words fixed in code, the locale's added from
     data-bh-w-* attributes), veil-first reorder on every device, shop / help / guide rows with their limits, the no-match
     rule (no products, shop rows or help rows; a Contact row rides along) and counts for a status line. The English-only
     "Shop " prefix and verb regex are gone. Always asks Shopify for 10 products (was 6, 10 on a phone veil query);
     the menu rows now work on desktop too. renderResults drew the model with the old markup; stage 2 re-laid it out.
   - 13 Oct 2026: search redesign v2, stage 2 (the layout). One sheet for every width; ONE desktop input (the bar opens the
     sheet, the sheet's input is the source, both keep their text, Clear, reopen from cache, the sheet survives the compact
     bar); phones get the dialog (role, aria-modal, name), the top band with X and Clear, ONE scroller sized to the visual
     viewport, and the Your Britten row (own class, so menuRow stays the drawer's). renderResults draws the new groups (lead,
     products as squares or rows, Shop, Help, Guides, Start here / Order samples beside results on desktop, no-match with
     Popular and Contact) from the model; Popular is a link list (.bh-pop), Bestsellers is read from the Liquid and its titles
     parsed. Arrow keys walk an explicit list (and the old .bh-pill fallback that could focus a hidden element is gone). One
     visually hidden status line (BHSearch.statusLine) replaces the aria-live results. search_click analytics. updateWorkshop()
     runs when search opens. Esc no longer empties the field. Removed: .bh--bar-search, .bh--bare, --bh-search-x and its resize
     listener, searchBarMode, syncBare, the Popular chips, the phone quick rows, the "Cancel" text and data-bh-s-none.
   - 13 Oct 2026: three fixes found on the dev preview. (1) Phones: the layer no longer shrinks to the visual viewport (on
     iOS 26 the translucent accessory bar and keyboard then showed the page behind it); it always covers the layout viewport
     and fitViewport writes --bh-kb (the part of it the visual viewport does not reach, the scroller's bottom padding) and
     --bh-kt (the visual viewport's offsetTop, the layer's top padding, so the band stays pinned). (2) Arrow Down / Up do
     nothing while the typed text's results are still on the way (asking), instead of walking the empty state's Popular.
     (3) BHSearch.guideRows drops a guide whose key (folded label, punctuation out, words of two characters or fewer out)
     matches one already taken ("What is a Cathedral Length Veil?" / "What is Cathedral Length Veil?").
   - 14 Oct 2026: search redesign v3, the desktop Search button. The bar's search form and field are gone (barForm, barInput,
     suppressBarFocus, openFromBar, the field's mousedown / focus / click / input / keydown handlers, copying its text into the
     sheet and mirroring the sheet's back, the compact icon button); in their place one link, [data-bh-search-btn], serves the
     editorial and the compact bar. Click, Enter or Space opens the sheet (Space is handled here: a link with role=button
     does not click on Space; a modified click, ctrl / cmd / shift / alt or a middle button, is left to the browser, so it
     opens /search in a new tab); Search again closes; Esc, Close and Search again return focus to it. search_open source
     is button | menu. On the /search page the opener focuses [data-bh-page-input] instead of opening the sheet (falls
     back to the sheet when there is none). Search URLs carry type=product: the sheet's form (hidden field), See all and
     the Popular links without a path; the sheet's submit and See all send BHSearch.expandQuery(typed), as predictive
     search already does, so "isabella" lands on real results.
   - 3 Nov 2026: search guides are real guides only. Shopify's page results become guides only if on GUIDE_HANDLES (new, with
     BHSearch.isGuide) and its title shares a word with the query (BHSearch.guideNamer for the results page); old bride stories,
     legal pages and guides that only mention the word in passing no longer show. Sarah's note.
   ========================================================================== */
(function () {
  'use strict';

  var BH_MOBILE_STICKY = true;   // one-line kill switch for the phone's compact sticky bar
  var DEBUG = false;

  var DESKTOP_QUERY = '(min-width: 1024px)';
  var COMPACT_H = 65;            // px: the compact bar including its 1px border
  var SHOW_AFTER = 40;           // px past the editorial header's bottom edge
  var RETURN_BEFORE = 20;        // px before that edge
  var HIDE_AFTER = 400;          // px past the edge; hide on scroll down
  var DEAD_BAND = 3;             // px of scroll ignored when reading direction
  var HOVER_OPEN_MS = 120;
  var HOVER_CLOSE_MS = 200;
  var LOC_GUTTER = 16;           // px the picker popover keeps clear of the viewport edges
  var DIM_AFTER_MS = 250;        // a mega panel dims the page only once it has stayed open this long
  var ANNOUNCE_MS = 5000;
  var ANNOUNCE_FIRST_MS = 9000;   // slide 1 (index 0) stays longer than the rest
  var ANNOUNCE_SLIDE_MS = 600;   // keep in step with the track transition in the CSS
  var SUGGEST_DEBOUNCE_MS = 200;
  var SUGGEST_LIMIT = 10;        // always ask for the most Shopify allows: the veil-first reorder and the lead need room, BHSearch shows 6
  var SUGGEST_MIN_CHARS = 2;
  var WS_TZ = 'Europe/London';   // the workshop's clock (new mobile menu, "Ask the workshop")
  var WS_FROM = 9 * 60;          // opens 09:00
  var WS_TO = 16 * 60;           // closed from 16:00 (the footer card's rule, assets/britten-footer.js)
  var M_BAR_H = 56;              // px: the phone bar (the spacer already holds this height)
  var M_SHOW_AFTER = 80;         // px past the header's bottom edge before the phone bar goes fixed
  var M_HYST = 8;                // px of scroll in one direction before the phone bar hides / shows

  function log(msg) {
    if (DEBUG && window.console) console.log('[Britten Header] ' + msg);
  }

  // Analytics: gtag, dataLayer and Shopify's analytics bus, each only if present. No personal data in any payload.
  function bhTrack(name, payload) {
    payload = payload || {};
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
      var ev = { event: name };
      Object.keys(payload).forEach(function (k) { ev[k] = payload[k]; });
      (window.dataLayer = window.dataLayer || []).push(ev);
    } catch (e) { /* analytics must never break the header */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, payload);
    } catch (e) { /* same */ }
  }

  var mqDesktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
  function isDesktop() {
    return mqDesktop ? mqDesktop.matches : true;
  }

  function stickyHeight() {
    return isDesktop() ? COMPACT_H : 0;
  }
  window.brittenHeaderStickyHeight = stickyHeight;

  // Hooks the stub below points at once init has run.
  var api = { openSearch: null, closeSearch: null, openLoc: null, openLocFor: null, applyLoc: null };

  // The other country / language controls (footer, shipping bar, PDP delivery estimate, cart
  // delivery) go through BrittenLocPref.open(code); when this header's picker is on, that
  // hands over here. It returns false when it cannot show the panel (no panel on the page, or
  // below 1024px, where the old bottom sheet stays), so the caller falls back to the old popover.
  window.BrittenHeaderLoc = {
    open: function (code) { return api.openLocFor ? api.openLocFor(code || null) : false; },
    // Switch country and language with the panel's own submit, without opening the panel
    // (the first-visit language suggestion, snippets/britten-locale-suggestion.liquid).
    // Returns false when the header has not set up yet, so the caller can use its own form.
    apply: function (code, locale) { return api.applyLoc && code ? api.applyLoc(code, locale || null) : false; }
  };

  /* -----------------------------------------------------------------------
     The old Flex scripts (app.js, utilities.js, z__jsProduct.js) call
     Shopify.theme.jsHeader.<something> from resize handlers and product
     setup. That object is created by the old header section, which does not
     render when this header is on. An inert stand-in keeps them from
     throwing. It is only installed when the real one is missing.
     ----------------------------------------------------------------------- */
  function installLegacyStub() {
    var S = window.Shopify = window.Shopify || {};
    S.theme = S.theme || {};
    if (S.theme.jsHeader) return;
    var noop = function () {};
    S.theme.jsHeader = {
      enable_sticky: false,
      enable_overlay: false,
      header_layout: 'centered',
      init: noop,
      unload: noop,
      enableSticky: noop,
      disableSticky: noop,
      enableOverlayStyle: noop,
      disableOverlayStyle: noop,
      updateOverlayStyle: noop,
      sectionUnderlayIsImage: function () { return false; },
      showStickyMenu: noop,
      hideStickyMenu: noop,
      showSearch: function () { if (api.openSearch) api.openSearch(); },
      hideSearch: function () { if (api.closeSearch) api.closeSearch(); },
      getHeaderHeight: stickyHeight
    };
  }
  installLegacyStub();

  /* -----------------------------------------------------------------------
     Small helpers
     ----------------------------------------------------------------------- */
  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function focusVisible(el) {
    try {
      return !!el && el.matches(':focus-visible');
    } catch (e) {
      return true; // no :focus-visible support: treat every focus as keyboard
    }
  }

  function isShown(el) {
    return !!(el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length));
  }

  function focusables(layer) {
    return toArray(layer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')).filter(isShown);
  }

  function make(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  // Accept only same-site paths from the suggest endpoint.
  function safeUrl(u) {
    if (typeof u !== 'string' || !u) return '#';
    if (u.charAt(0) === '/' && u.charAt(1) !== '/') return u;
    try {
      var a = new URL(u, window.location.href);
      if (a.origin === window.location.origin) return a.pathname + a.search + a.hash;
    } catch (e) { /* fall through */ }
    return '#';
  }

  function sized(u, w) {
    if (typeof u !== 'string' || !u) return '';
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  /* -----------------------------------------------------------------------
     SEARCH LOGIC (13 Oct 2026, search redesign v2, stage 1): window.BHSearch

     One self-contained block. The functions from `fold` down to `buildModel` touch no DOM and
     no globals, so scripts/header-search-test.js can run THIS file in a vm sandbox against
     recorded Shopify responses (scripts/fixtures/header-search/). The two readers at the
     bottom (readMenu, parseList) only READ the nodes / strings they are handed.

     Everything customer-visible in the model is data: strings reach the DOM renderer through
     the model, and every English word list below is fixed in code ("English always") with the
     locale's own words ADDED from ctx.words (data attributes on .bh, locale keys
     britten.header.search_words_*), so no translation can change an English rule and an
     untranslated locale degrades to English.

     ENTRY POINTS
       BHSearch.expandQuery(typed)               -> the query to SEND (aliases applied; the box never changes)
       BHSearch.buildModel(response, typed, ctx) -> model (below)
       BHSearch.readMenu(drawerRoot)             -> ctx.menu from the drawer's [data-bh-mm] node
       BHSearch.statusLine(model.counts, tpl)    -> "6 products, 1 collection" (tpl: the localised templates)

     ctx (built once at init; buildModel compiles it on first use and caches the result on it)
       lang       'en', 'es' ... the page language (Spanish plurals also take -es)
       searchUrl  '/search' (market prefixed), for model.seeAll.href
       menu       { sections: [{ id, label }],
                    links: [{ label, href, section, group, kind, live? }] }   as BHSearch.readMenu returns
                  kind: 'link' (level-2 row), 'item' (row inside a group), 'compare' (the "Compare ..." guide
                  row), 'utility' (Where is my order? / Contact us)
       words      { returns, shipping, contact, help, verbs, guide, products, ignore, veil }
                  each an array of phrases (or a comma-separated string) of THIS locale's words; English is added
       subs       { returns, shipping }   the help rows' sub-lines (locale keys search_help_*_sub)

     MODEL  (buildModel returns exactly this; stage 2 renders from it)
       intent    'service' | 'mixed' | 'guide' | 'verb' | 'plain'
                 service  a service phrase and no product word: products, shop rows and guides are all empty
                 mixed    a service phrase beside a product word: products kept, help has exactly ONE row
                 guide    "help choosing a veil", "which veil", "veil guide": products kept, guides filled
                 verb     "order"/"buy" before a product word: products kept, NO help row
                 plain    everything else
       lead      null, or a product (below): the product whose parsed name equals the query (or its alias), folded,
                 one typo allowed from 5 characters. NEVER also in `products`.
       products  Shopify's order after the veil-first reorder, the lead removed; lead + products <= 6
       shop      <= 2 rows { label, href, sub, kind: 'menu' | 'collection' }, sub is "Veils · Veil lengths" (menu rows)
       help      service: up to 3 rows, mixed: exactly 1, otherwise [].
                 { id: 'returns' | 'shipping' | 'contact', label, href, sub, live }   live = the Contact row, whose
                 sub-line is the workshop status line (stage 2 copies it from the drawer's [data-bh-ws-text])
       guides    <= 2 rows { label, href, kind: 'menu' | 'page' }; the drawer's own page / blog links first, then
                 Shopify page results on the GUIDE_HANDLES list whose title shares a word with the query (none of those
                 when there is a lead); deduplicated by path
       seeAll    { query, href }          always. query is what she typed (the label); href is the results page for the
                                  ALIASED query (model.sent) with type=product, like the sheet's own submit
       noMatch   true only with no lead, no products, no shop rows and no help rows
       noMatchHelp  [ the Contact row ] when noMatch (artboard P7), else []
       counts    { products (lead included), shop, help, guides }
       sent      the aliased query that was (or would be) sent
       product   { title, name, style, url, image, price, available, handle }
                 name / style: BHSearch.parseTitle; style is sentence-cased for display. url is Shopify's raw
                 (relative, carries _pos= tracking) so the renderer runs it through safeUrl(); price is Shopify's
                 plain number string for priceText().
     ----------------------------------------------------------------------- */
  var BHSearch = (function () {
    var LIMITS = { products: 6, shop: 2, helpService: 3, helpMixed: 1, guides: 2 };

    // The only pages search may call a guide (3 Nov 2026, Sarah: the rest are old blog posts and bride stories, not guides).
    // By handle, because a handle is the same in every language. A new guide page shows in search once its handle is here.
    var GUIDE_HANDLES = {};
    (
      'wedding-veil-guide veil-length-options find-your-veil-length how-to-choose-the-right-length-and-width-veil ' +
      'different-names-for-veils what-are-veils-made-from looking-after-your-veil ' +
      'veil-faqs-how-do-you-store-a-wedding-veil can-you-wear-a-veil-under-your-hair ' +
      'do-brides-have-to-wear-a-wedding-veil how-to-secure-attach-drop-wedding-veil ' +
      'what-is-a-ballet-length-veil what-is-a-bandeau-veil what-is-a-birdcage-veil what-is-a-blusher-veil ' +
      'what-is-a-cathedral-length-veil what-is-a-church-length-veil what-is-a-cut-edge-wedding-veil ' +
      'what-is-a-drop-veil what-is-a-fingertip-length-veil what-is-a-floor-length-veil what-is-a-juliet-cap-veil ' +
      'what-is-a-mantilla-veil what-is-a-russian-veil what-is-a-semi-edge-veil what-is-a-single-tier-wedding-veil ' +
      'what-is-an-elbow-length-veil what-is-two-tier-veil ' +
      'wedding-accessories-guide wedding-hair-accessories-guide how-to-choose-a-bridal-headpiece ' +
      'what-are-bridal-hair-vines what-are-bridal-head-chains what-are-bridal-head-combs what-are-bridal-shoe-clips ' +
      'what-is-a-bridal-cover-up how-to-add-colour-to-your-bridal-outfit ' +
      'wedding-jewellery-to-match-your-dress what-is-gold-filled-jewellery how-to-care-for-gold-filled-jewellery ' +
      'how-to-care-for-sterling-silver-jewellery how-to-choose-your-bracelet-length ' +
      'how-to-choose-your-necklace-chain-length ' +
      'what-is-a-garter how-to-choose-a-wedding-garter your-bridal-garter-questions-answered ' +
      'what-is-the-tradition-behind-the-wedding-garter who-should-buy-the-wedding-garter-for-the-bride ' +
      'from-petite-garters-to-plus-size-garters'
    ).split(' ').forEach(function (h) { GUIDE_HANDLES[h] = true; });
    function isGuide(url) {
      var m = /\/pages\/([^\/?#]+)/.exec(String(url || ''));
      return !!m && GUIDE_HANDLES[m[1]] === true;
    }
    var SHOP_SECTIONS = ['veils', 'jewellery', 'hair', 'belts', 'garters'];

    // English is fixed here and always on; a locale only adds to it.
    var EN_WORDS = {
      returns: ['return', 'refund', 'return policy'],
      shipping: ['delivery', 'shipping', 'postage', 'tracking', 'order status', 'my order', 'where is my order'],
      contact: ['contact', 'email', 'phone'],
      help: ['help'],                       // alone = Contact; inside "help choosing a veil" it is just a word
      verbs: ['order', 'buy'],
      guide: ['help choosing', 'help me choose', 'which veil', 'veil guide', 'how to choose', 'guide'],
      products: ['veil', 'sample', 'garter', 'earring', 'comb', 'hook'],
      ignore: ['veil', 'wedding', 'bridal', 'a', 'the'],
      veil: ['veil']
    };
    var STOP = ['all', 'shop', 'and', 'for', 'with', 'our', 'your', 'you', 'this', 'that', 'from', 'not'];

    // The words customers use that the catalogue does not. Whole-word, case-insensitive; only the query SENT is
    // rewritten, never the text in the box. Longest phrase first. (Applied to the lead match too.)
    var SYNONYMS = [
      [/\bfree swatch samples\b/g, 'samples'],
      [/\bswatches\b/g, 'samples'],
      [/\bswatch\b/g, 'samples'],
      [/\bhangers?\b/g, 'hanging hook'],
      // Isabella was the silk-style single tier cut edge veil, now one product with its materials as options
      [/\bisabella\b/g, 'single tier cut edge veil']
    ];

    // Lowercase, accents and combining marks off, spaces collapsed and trimmed.
    function fold(s) {
      s = String(s == null ? '' : s).toLowerCase();
      if (s.normalize) s = s.normalize('NFD');
      return s.replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').replace(/^ | $/g, '');
    }
    function tokens(s) {
      return fold(s).split(/[^a-z0-9\u0100-\uffff]+/).filter(function (t) { return !!t; });
    }
    function expandQuery(query) {
      var q = String(query == null ? '' : query).toLowerCase();
      SYNONYMS.forEach(function (pair) { q = q.replace(pair[0], pair[1]); });
      return q;
    }
    // What the results page is asked for: the alias when one fires ("isabella" -> the real product words), else the
    // words exactly as typed, so the page's input keeps her capitals ("Sandy", not "sandy").
    function pageQuery(query) {
      var typed = String(query == null ? '' : query).replace(/^\s+|\s+$/g, '');
      var sent = expandQuery(typed);
      return sent === typed.toLowerCase() ? typed : sent;
    }

    // The forms a token can be matched as: itself and its singular. English strips a trailing "s"; Spanish also "es"
    // (velos, colores). Both sides of every comparison go through the same function, so a list word and a typed
    // word meet whichever of them is the plural.
    function formsOf(tok, es) {
      var out = [tok];
      var n = tok.length;
      if (n > 3 && tok.charAt(n - 1) === 's' && tok.charAt(n - 2) !== 's') out.push(tok.slice(0, -1));
      if (es && n > 4 && tok.slice(-2) === 'es') out.push(tok.slice(0, -2));
      return out;
    }
    function sharesForm(a, b) {
      for (var i = 0; i < a.length; i++) if (b.indexOf(a[i]) !== -1) return true;
      return false;
    }

    // At most one typo: one letter wrong, missing, extra, or two neighbours swapped (Damerau-Levenshtein <= 1).
    function withinOne(a, b) {
      if (a === b) return true;
      var la = a.length, lb = b.length;
      if (Math.abs(la - lb) > 1) return false;
      var i = 0;
      while (i < la && i < lb && a.charAt(i) === b.charAt(i)) i++;
      if (la === lb) {
        if (a.slice(i + 1) === b.slice(i + 1)) return true;
        return i + 1 < la && a.charAt(i) === b.charAt(i + 1) && a.charAt(i + 1) === b.charAt(i) && a.slice(i + 2) === b.slice(i + 2);
      }
      return la > lb ? a.slice(i + 1) === b.slice(i) : b.slice(i + 1) === a.slice(i);
    }

    /* ---- name parse (decision 5) ----------------------------------------------------------------------
       " - 'Sandy'" / "- 'Rosaline'" / " — 'Maeve'" / " - «Skylar»" / "...espalda «Charlotte»" (Spanish)  -> name + style
       "...Extra Wide- Blair", "...veil - Beatrice", "...Flowers- Petal Drift"                              -> name + style
       otherwise split at the first " - " or "(" ("Tulle garter (various colours)"), but a bracket of 3 characters or
       fewer stays in the name ("Extra Veil Length (cm)"); no split: the title is the name. Words are reordered,
       never dropped. */
    var RE_QUOTED = /^(.*?)\s*[-\u2013\u2014]\s*['\u2018\u2019"\u201c\u00ab]([^'"\u2018\u2019\u201c\u201d\u00ab\u00bb]{1,40}?)['\u2018\u2019"\u201d\u00bb]\s*$/;
    var RE_GUILLEMET = /^(.*?)\s*\u00ab([A-Z\u00c0-\u00dd][^\u00ab\u00bb'"]{0,39}?)\u00bb\s*$/;
    var RE_ATTACHED = /^(.*\S)-\s+([A-Z\u00c0-\u00dd][A-Za-z\u00c0-\u017f'\u2019]*(?: [A-Z\u00c0-\u00dd][A-Za-z\u00c0-\u017f'\u2019]*)?)$/;
    var RE_SPACED = /^(.*\S)\s+-\s+([A-Z\u00c0-\u00dd][A-Za-z\u00c0-\u017f'\u2019]*)$/;

    // Sentence case for the style line: the first letter up, then Title Case words down ("Various Materials" ->
    // "Various materials"). ALL CAPS and words with their own capitals are left alone.
    function sentenceCase(s) {
      s = String(s == null ? '' : s).replace(/\s+/g, ' ').replace(/^ | $/g, '');
      if (!s) return '';
      var out = s.split(' ').map(function (w, i) {
        var m = /^([^A-Za-z\u00c0-\u017f]*)(.*?)([^A-Za-z\u00c0-\u017f]*)$/.exec(w);
        var core = m ? m[2] : w;
        if (i > 0 && /^[A-Z\u00c0-\u00dd][a-z\u00df-\u00ff\u0100-\u017f'\u2019]+(?:-[A-Z\u00c0-\u00dd][a-z\u00df-\u00ff\u0100-\u017f'\u2019]+)*$/.test(core)) {
          return m[1] + core.toLowerCase() + m[3];
        }
        return w;
      });
      var first = out[0];
      out[0] = first.replace(/^([^A-Za-z0-9\u00c0-\u017f]*)([A-Za-z\u00c0-\u017f])/, function (m, pre, ch) { return pre + ch.toUpperCase(); });
      return out.join(' ');
    }

    function parseTitle(title) {
      var t = String(title == null ? '' : title).replace(/\s+/g, ' ').replace(/^ | $/g, '');
      var m = RE_QUOTED.exec(t) || RE_GUILLEMET.exec(t);
      if (m && m[1].replace(/\s+$/, '')) return { name: m[2].replace(/^ | $/g, ''), style: sentenceCase(m[1]) };
      m = RE_ATTACHED.exec(t) || RE_SPACED.exec(t);
      if (m) return { name: m[2], style: sentenceCase(m[1]) };
      var iDash = t.indexOf(' - ');
      var iParen = t.indexOf('(');
      if (iDash > 0 && (iParen < 0 || iDash < iParen)) {
        var a = t.slice(0, iDash).replace(/ +$/, '');
        var b = t.slice(iDash + 3).replace(/^ +/, '');
        if (a && b) return { name: a, style: sentenceCase(b) };
      } else if (iParen > 0) {
        var rest = t.slice(iParen);
        var inner = /^\((.*)\)$/.exec(rest);
        var style = inner ? inner[1] : rest;
        var name = t.slice(0, iParen).replace(/ +$/, '');
        if (name && style.length > 3) return { name: name, style: sentenceCase(style) };
      }
      return { name: t, style: '' };
    }

    function toProduct(p) {
      p = p || {};
      var title = String(p.title == null ? '' : p.title);
      var parsed = parseTitle(title);
      return {
        title: title,
        name: parsed.name,
        style: parsed.style,
        url: p.url || '',
        image: p.image || (p.featured_image && p.featured_image.url) || '',
        price: p.price == null ? '' : p.price,
        available: p.available !== false,
        handle: p.handle || ''
      };
    }

    function pathOf(href) {
      return String(href || '').split('#')[0].split('?')[0].replace(/^\/[a-z]{2}-[a-z]{2}(?=\/)/i, '').replace(/\/+$/, '').toLowerCase();
    }
    function array(v) { return Object.prototype.toString.call(v) === '[object Array]' ? v : []; }
    function asList(v) {
      if (typeof v === 'string') v = v.split(',');
      return array(v).map(function (s) { return String(s).replace(/^\s+|\s+$/g, ''); }).filter(function (s) { return !!s; });
    }

    /* ---- compile ctx once --------------------------------------------------------------------------------- */
    function compile(ctx) {
      if (ctx && ctx._bh) return ctx._bh;
      ctx = ctx || {};
      var lang = String(ctx.lang || 'en').toLowerCase().slice(0, 2);
      var es = lang === 'es';
      var c = { ctx: ctx, es: es, lang: lang, W: {}, set: {} };
      var locale = ctx.words || {};

      function forms(tok) { return formsOf(tok, es); }
      c.forms = forms;
      // phrases: an array of arrays of token forms
      function phrases(list) {
        return list.map(function (p) { return tokens(p).map(forms); }).filter(function (p) { return p.length; });
      }
      function wordSet(list) {
        var set = {};
        list.forEach(function (p) { tokens(p).forEach(function (t) { forms(t).forEach(function (f) { set[f] = true; }); }); });
        return set;
      }
      Object.keys(EN_WORDS).forEach(function (k) {
        var list = EN_WORDS[k].concat(asList(locale[k]));
        c.W[k] = list;
        c.set[k] = wordSet(list);
      });
      c.phrases = {};
      ['returns', 'shipping', 'contact', 'help', 'verbs', 'guide'].forEach(function (k) { c.phrases[k] = phrases(c.W[k]); });

      // words that are never "the thing she is looking for": ignore, verbs, every service word, guide words, stop words
      var reserved = {};
      ['ignore', 'verbs', 'returns', 'shipping', 'contact', 'help', 'guide', 'veil'].forEach(function (k) {
        Object.keys(c.set[k]).forEach(function (f) { reserved[f] = true; });
      });
      STOP.forEach(function (w) { reserved[w] = true; });
      c.reserved = reserved;
      // for guide matching only the service / verb / ignore words drop out ("help", "choosing" and "guide" stay)
      var plain = {};
      ['ignore', 'verbs', 'returns', 'shipping', 'contact', 'veil'].forEach(function (k) {
        Object.keys(c.set[k]).forEach(function (f) { plain[f] = true; });
      });
      STOP.forEach(function (w) { plain[w] = true; });
      c.plain = plain;

      var menu = ctx.menu || { sections: [], links: [] };
      var secLabel = {};
      array(menu.sections).forEach(function (s) { secLabel[s.id] = s.label; });
      c.secLabel = secLabel;

      // drawer links, each with its words ready for scoring
      function isIn(set, t) {
        var f = forms(t);
        for (var i = 0; i < f.length; i++) if (set[f[i]]) return true;
        return false;
      }
      function formSet(text) {
        var set = {};
        tokens(text).forEach(function (t) { if (t.length > 2 && !isIn(reserved, t)) forms(t).forEach(function (f) { set[f] = true; }); });
        return set;
      }
      function guideSet(text) {
        var set = {};
        tokens(text).forEach(function (t) { if (t.length > 1 && !isIn(plain, t)) forms(t).forEach(function (f) { set[f] = true; }); });
        return set;
      }
      c.guideSet = guideSet;
      c.links = array(menu.links).map(function (l, i) {
        var path = pathOf(l.href);
        var isPage = /^\/(pages|blogs)\//.test(path);
        return {
          i: i, label: l.label, href: l.href, section: l.section, group: l.group, kind: l.kind, live: !!l.live,
          path: path, page: isPage, product: /^\/products\//.test(path),
          own: formSet(l.label), ctx: formSet((l.group || '') + ' ' + (secLabel[l.section] || '')),
          gOwn: guideSet(l.label)
        };
      });

      // product words: the fixed list, the locale's, the veil words, and every shop label in the drawer
      var product = {};
      Object.keys(c.set.products).forEach(function (f) { product[f] = true; });
      Object.keys(c.set.veil).forEach(function (f) { product[f] = true; });
      c.links.forEach(function (l) {
        if (SHOP_SECTIONS.indexOf(l.section) === -1 || l.page || l.product || (l.kind !== 'link' && l.kind !== 'item')) return;
        [l.label, l.group, secLabel[l.section]].forEach(function (text) {
          tokens(text).forEach(function (t) { if (t.length > 2 && !isIn(reserved, t)) forms(t).forEach(function (f) { product[f] = true; }); });
        });
      });
      c.product = product;

      // help rows, found by href so the labels can be translated
      var HELP_HREFS = {
        returns: /\/pages\/contact\?type=returns$/,
        shipping: /\/pages\/shipping-returns$/,
        contact: /\/pages\/contact$/
      };
      var subs = ctx.subs || {};
      c.help = {};
      Object.keys(HELP_HREFS).forEach(function (id) {
        for (var i = 0; i < c.links.length; i++) {
          if (HELP_HREFS[id].test(c.links[i].href || '')) {
            c.help[id] = { id: id, label: c.links[i].label, href: c.links[i].href, sub: subs[id] || '', live: id === 'contact' };
            return;
          }
        }
      });
      c.helpHrefs = HELP_HREFS;
      if (ctx && typeof ctx === 'object') {
        try { Object.defineProperty(ctx, '_bh', { value: c, enumerable: false, configurable: true }); } catch (e) { ctx._bh = c; }
      }
      return c;
    }

    /* ---- intent (spec "Intent" + decision 7) -------------------------------------------------------------- */
    function findPhrase(c, toks, list) {
      var qf = toks.map(c.forms);
      var best = -1;
      for (var p = 0; p < list.length; p++) {
        var ph = list[p];
        for (var i = 0; i + ph.length <= qf.length; i++) {
          var ok = true;
          for (var j = 0; j < ph.length; j++) if (!sharesForm(qf[i + j], ph[j])) { ok = false; break; }
          if (ok) { if (best < 0 || i < best) best = i; break; }
        }
      }
      return best;
    }
    function isProductToken(c, t) {
      var f = c.forms(t);
      for (var i = 0; i < f.length; i++) if (c.product[f[i]]) return true;
      return false;
    }
    // does the query hold a whole product name from this response ("sandy returns")?
    function holdsName(c, toks, names) {
      var qf = toks.map(c.forms);
      for (var n = 0; n < names.length; n++) {
        var nt = names[n];
        if (!nt.length) continue;
        var nf = nt.map(c.forms);
        for (var i = 0; i + nf.length <= qf.length; i++) {
          var ok = true;
          for (var j = 0; j < nf.length; j++) if (!sharesForm(qf[i + j], nf[j])) { ok = false; break; }
          if (ok) return true;
        }
      }
      return false;
    }

    // -> { intent, concepts: ['returns' | 'shipping' | 'contact' ...] in the order they appear in the query }
    function classify(c, toks, names) {
      var hits = [];
      function note(id, at) { if (at >= 0) hits.push({ id: id, at: at }); }
      note('returns', findPhrase(c, toks, c.phrases.returns));
      note('shipping', findPhrase(c, toks, c.phrases.shipping));
      note('contact', findPhrase(c, toks, c.phrases.contact));
      // "help" on its own means Contact; inside "help choosing a veil" it is only a word
      if (toks.length === 1 && findPhrase(c, toks, c.phrases.help) === 0) note('contact', 0);
      // a bare "order" (nothing else but filler words) keeps meaning "where is my order"
      var meaningful = toks.filter(function (t) { return !sharesForm(c.forms(t), Object.keys(c.set.ignore)); });
      if (!hits.length && meaningful.length === 1 && sharesForm(c.forms(meaningful[0]), ['order'])) note('shipping', 0);
      hits.sort(function (a, b) { return a.at - b.at; });
      var concepts = [];
      hits.forEach(function (h) { if (concepts.indexOf(h.id) === -1) concepts.push(h.id); });

      var productAt = -1;
      for (var i = 0; i < toks.length; i++) { if (isProductToken(c, toks[i])) { productAt = i; break; } }
      var hasProduct = productAt >= 0 || holdsName(c, toks, names);

      if (concepts.length) return { intent: hasProduct ? 'mixed' : 'service', concepts: concepts };
      if (findPhrase(c, toks, c.phrases.guide) >= 0) return { intent: 'guide', concepts: [] };
      var verbAt = findPhrase(c, toks, c.phrases.verbs);
      if (verbAt >= 0) {
        for (var k = verbAt + 1; k < toks.length; k++) if (isProductToken(c, toks[k])) return { intent: 'verb', concepts: [] };
        if (holdsName(c, toks.slice(verbAt + 1), names)) return { intent: 'verb', concepts: [] };
      }
      return { intent: 'plain', concepts: [] };
    }

    function queryWords(c, toks, drop) {
      return toks.filter(function (t) {
        if (t.length < 2) return false;
        return !sharesForm(c.forms(t), Object.keys(drop));
      });
    }
    function score(c, words, own, other) {
      var s = 0;
      words.forEach(function (w) {
        var f = c.forms(w);
        if (sharesForm(f, Object.keys(own))) s += 2;
        if (other && sharesForm(f, Object.keys(other))) s += 1;
      });
      return s;
    }

    // shop rows: the drawer's links (not pages), best score first; ties keep document order
    function shopRows(c, words) {
      if (!words.length) return [];
      var seen = {};
      var scored = [];
      c.links.forEach(function (l) {
        if (SHOP_SECTIONS.indexOf(l.section) === -1 || l.page || l.product || (l.kind !== 'link' && l.kind !== 'item')) return;
        var s = score(c, words, l.own, l.ctx);
        if (s > 0) scored.push({ s: s, i: l.i, l: l });
      });
      scored.sort(function (a, b) { return (b.s - a.s) || (a.i - b.i); });
      var rows = [];
      scored.forEach(function (d) {
        if (seen[d.l.path]) return;
        seen[d.l.path] = true;
        var sec = c.secLabel[d.l.section] || '';
        rows.push({ label: d.l.label, href: d.l.href, sub: [sec, d.l.group].filter(function (x) { return !!x; }).join(' \u00b7 '), kind: 'menu' });
      });
      return rows;
    }

    // A guide's identity for "is this the same guide twice": the label folded, punctuation gone, words of two characters or
    // fewer dropped ("What is a Cathedral Length Veil?" and "What is Cathedral Length Veil?" are one guide). A label with no
    // word left keeps its folded text, so two odd labels never collapse into one empty key.
    function guideKey(label) {
      var key = tokens(label).filter(function (t) { return t.length > 2; }).join(' ');
      return key || fold(label);
    }

    // guides: the drawer's page / blog links whose label shares a word with the query, then Shopify's page results that are
    // real guides (isGuide) and whose title shares a word with the query. A guide whose key matches one already taken is dropped (Shopify can hold two pages that differ
    // by an article).
    function guideRows(c, intent, gwords, allWords, pages) {
      var rows = [];
      var seen = {};
      var seenKey = {};
      function help(href) {
        var h = c.helpHrefs;
        return h.returns.test(href) || h.shipping.test(href) || h.contact.test(href) || /\/pages\/contact\?type=/.test(href);
      }
      var scored = [];
      c.links.forEach(function (l) {
        if (!l.page || (l.kind !== 'link' && l.kind !== 'item') || help(l.href)) return;
        var s = score(c, gwords, l.gOwn, null);
        if (s > 0) scored.push({ s: s, i: l.i, l: l });
      });
      scored.sort(function (a, b) { return (b.s - a.s) || (a.i - b.i); });
      // A guide request ("which veil") always gets the main guide first. The drawer has it twice ("Help choosing a veil"
      // under Veils, "Veil guide" under About): the one whose label shares most with what was typed, else the first.
      if (intent === 'guide') {
        var main = null;
        c.links.forEach(function (l) {
          if (!/\/pages\/wedding-veil-guide$/.test(l.href) || l.kind === 'compare') return;
          var sc = score(c, allWords, l.gOwn, null);
          if (!main || sc > main.s) main = { s: sc, l: l };
        });
        if (main) scored.unshift({ s: 99, i: main.l.i, l: main.l });
      }
      scored.forEach(function (d) {
        var key = guideKey(d.l.label);
        if (seen[d.l.path] || seenKey[key]) return;
        seen[d.l.path] = true;
        seenKey[key] = true;
        rows.push({ label: d.l.label, href: d.l.href, kind: 'menu' });
      });
      pages.forEach(function (p) {
        var path = pathOf(p && p.url);
        if (!p || !p.title || !path || seen[path] || !isGuide(path)) return;
        if (score(c, gwords, c.guideSet(p.title), null) <= 0) return; // Shopify matches the body too: the title must name what she typed
        var key = guideKey(p.title);
        if (seenKey[key]) return;
        seen[path] = true;
        seenKey[key] = true;
        rows.push({ label: String(p.title), href: p.url, kind: 'page' });
      });
      return rows;
    }

    // For the results page, which reads its guides from a section: BHSearch's own test for "this title names what she typed".
    function guideNamer(query, ctx) {
      var c = compile(ctx || {});
      var words = queryWords(c, tokens(query), c.plain);
      return function (title) { return score(c, words, c.guideSet(title), null) > 0; };
    }

    function helpRows(c, concepts, intent) {
      var order = [];
      concepts.forEach(function (id) {
        var list = id === 'returns' ? ['returns', 'shipping', 'contact'] : (id === 'shipping' ? ['shipping', 'contact'] : ['contact']);
        list.forEach(function (x) { if (order.indexOf(x) === -1) order.push(x); });
      });
      var rows = [];
      order.forEach(function (id) { if (c.help[id]) rows.push(c.help[id]); });
      return rows.slice(0, intent === 'mixed' ? LIMITS.helpMixed : LIMITS.helpService).map(function (r) {
        return { id: r.id, label: r.label, href: r.href, sub: r.sub, live: r.live };
      });
    }

    // For a "veil" query, titles with "veil" come first; everything else keeps Shopify's order (decorated sort, stable)
    function veilsFirst(c, typed, products) {
      var veil = Object.keys(c.set.veil);
      function hasVeil(text) {
        var f = fold(text);
        return veil.some(function (w) { return f.indexOf(w) !== -1; });
      }
      if (!hasVeil(typed)) return products;
      return products.map(function (p, i) {
        return { p: p, i: i, v: hasVeil(p.title) ? 0 : 1 };
      }).sort(function (a, b) { return (a.v - b.v) || (a.i - b.i); }).map(function (d) { return d.p; });
    }

    // The lead: exact name first, then one typo (only from 5 characters). The query, its alias, and each with a trailing
    // generic word taken off ("sandy veil" -> "sandy") are the candidates.
    function leadIndex(c, typed, sent, products) {
      var generic = {};
      Object.keys(c.set.veil).forEach(function (w) { generic[w] = true; });
      ['wedding', 'bridal'].forEach(function (w) { generic[w] = true; });
      var cands = [];
      function add(s) { if (s && cands.indexOf(s) === -1) cands.push(s); }
      [fold(typed), fold(sent)].forEach(function (q) {
        add(q);
        var t = q.split(' ');
        while (t.length > 1 && sharesForm(c.forms(t[t.length - 1]), Object.keys(generic))) t.pop();
        add(t.join(' '));
      });
      var i, k;
      for (i = 0; i < products.length; i++) {
        for (k = 0; k < cands.length; k++) if (products[i].nameFold === cands[k]) return i;
      }
      for (i = 0; i < products.length; i++) {
        for (k = 0; k < cands.length; k++) if (cands[k].length >= 5 && withinOne(products[i].nameFold, cands[k])) return i;
      }
      return -1;
    }

    function plainProduct(p) {
      return { title: p.title, name: p.name, style: p.style, url: p.url, image: p.image, price: p.price, available: p.available, handle: p.handle };
    }

    function buildModel(response, query, ctx) {
      var c = compile(ctx);
      var typed = String(query == null ? '' : query).replace(/^\s+|\s+$/g, '');
      var sent = expandQuery(typed);
      var found = (response && response.resources && response.resources.results) || {};
      var products = array(found.products).map(toProduct);
      products.forEach(function (p) { p.nameFold = fold(p.name.replace(/['\u2018\u2019"\u201c\u201d\u00ab\u00bb]/g, '')); });
      var collections = array(found.collections);
      var pages = array(found.pages);

      var toks = tokens(sent);
      var names = products.map(function (p) { return tokens(p.name); });
      var cls = classify(c, toks, names);
      var help = (cls.intent === 'service' || cls.intent === 'mixed') ? helpRows(c, cls.concepts, cls.intent) : [];
      var intent = cls.intent;
      // a service question we cannot answer (no help links in the page) must not hide the products
      if (intent === 'service' && !help.length) intent = 'plain';

      var model = {
        intent: intent, lead: null, products: [], shop: [], help: help, guides: [],
        seeAll: { query: typed, href: String((ctx && ctx.searchUrl) || '/search') + '?q=' + encodeURIComponent(pageQuery(typed)) + '&type=product' },
        noMatch: false, noMatchHelp: [], counts: { products: 0, shop: 0, help: help.length, guides: 0 }, sent: sent
      };

      if (intent !== 'service') {
        var ordered = veilsFirst(c, typed, products);
        var at = leadIndex(c, typed, sent, ordered);
        var lead = at >= 0 ? ordered[at] : null;
        var rest = ordered.filter(function (p) { return p !== lead; });
        model.lead = lead ? plainProduct(lead) : null;
        model.products = rest.slice(0, LIMITS.products - (lead ? 1 : 0)).map(plainProduct);

        var shopWords = queryWords(c, toks, c.reserved);
        var shop = shopRows(c, shopWords);
        var have = {};
        shop.forEach(function (r) { have[pathOf(r.href)] = true; });
        collections.forEach(function (col) {
          var p = pathOf(col && col.url);
          if (!col || !col.title || !p || have[p]) return;
          have[p] = true;
          shop.push({ label: String(col.title), href: col.url, sub: '', kind: 'collection' });
        });
        model.shop = shop.slice(0, LIMITS.shop);
        // for a guide request the words that SAY "guide" ("help", "choosing", "which") do not pick the other guides
        var skip = {};
        Object.keys(c.plain).forEach(function (k) { skip[k] = true; });
        if (intent === 'guide') Object.keys(c.set.guide).forEach(function (k) { skip[k] = true; });
        // a lead (the product she named) takes the sheet: no Shopify pages beside it (artboard D3); the drawer's own guide links
        // for a guide request still show
        model.guides = guideRows(c, intent, queryWords(c, toks, skip), queryWords(c, toks, c.plain), model.lead ? [] : pages).slice(0, LIMITS.guides);
      }

      model.counts.products = (model.lead ? 1 : 0) + model.products.length;
      model.counts.shop = model.shop.length;
      model.counts.guides = model.guides.length;
      model.noMatch = !model.lead && !model.products.length && !model.shop.length && !help.length;
      if (model.noMatch && c.help.contact) {
        var ct = c.help.contact;
        model.noMatchHelp = [{ id: ct.id, label: ct.label, href: ct.href, sub: ct.sub, live: ct.live }];
      }
      return model;
    }

    // "6 products, 1 collection": tpl = { productOne, productOther, collectionOne, collectionOther, helpOne, helpOther },
    // each a localised template with {{ n }}. Counts only; nothing is read from the results.
    function statusLine(counts, tpl) {
      tpl = tpl || {};
      counts = counts || {};
      var parts = [];
      function add(n, one, other) {
        var t = n === 1 ? one : other;
        if (n > 0 && t) parts.push(String(t).replace(/\{\{\s*n\s*\}\}/g, String(n)));
      }
      add(counts.products, tpl.productOne, tpl.productOther);
      add(counts.shop, tpl.collectionOne, tpl.collectionOther);
      add(counts.help, tpl.helpOne, tpl.helpOther);
      return parts.join(', ');
    }

    /* ---- readers: they only READ the node / string they are given ---------------------------------------- */
    function parseList(s) { return asList(s); }

    function hasClass(el, name) {
      return (' ' + ((el.getAttribute && el.getAttribute('class')) || '') + ' ').indexOf(' ' + name + ' ') !== -1;
    }
    function kids(el) { return el && el.children ? Array.prototype.slice.call(el.children) : []; }
    function isTag(el, tag) { return String(el.tagName || '').toLowerCase() === tag; }
    function textOf(el) { return String((el && el.textContent) || '').replace(/\s+/g, ' ').replace(/^ | $/g, ''); }
    function firstWithClass(el, name) {
      var ks = kids(el);
      for (var i = 0; i < ks.length; i++) {
        if (hasClass(ks[i], name)) return ks[i];
        var deep = firstWithClass(ks[i], name);
        if (deep) return deep;
      }
      return null;
    }
    function firstSpanText(btn) {
      var ks = kids(btn);
      for (var i = 0; i < ks.length; i++) if (isTag(ks[i], 'span')) return textOf(ks[i]);
      return textOf(btn);
    }

    // The m2 drawer ([data-bh-mm]) -> ctx.menu. Sections and groups come from the markup, so the labels are whatever
    // the page was rendered in (translated); nothing here is English.
    function readMenu(mm) {
      var out = { sections: [], links: [] };
      if (!mm) return out;
      function push(a, kind, section, group) {
        var href = a.getAttribute('href') || '';
        if (!href) return;
        var title = firstWithClass(a, kind === 'compare' ? 'bh-mg__guide-t' : 'bh-ml__t');
        var l = { label: textOf(title || a), href: href, section: section, group: group, kind: kind };
        if (a.getAttribute('data-bh-ws') !== null) l.live = true;
        out.links.push(l);
      }
      function walk(node, section, group) {
        kids(node).forEach(function (ch) {
          if (isTag(ch, 'a')) {
            if (hasClass(ch, 'bh-mg__guide')) push(ch, 'compare', section, group);
            else if (hasClass(ch, 'bh-mg__a')) push(ch, 'item', section, group);
            else if (hasClass(ch, 'bh-ml')) push(ch, 'link', section, group);
          } else if (hasClass(ch, 'bh-mg')) {
            var btn = firstWithClass(ch, 'bh-mg__btn');
            walk(firstWithClass(ch, 'bh-mg__list') || ch, section, btn ? firstSpanText(btn) : '');
          }
        });
      }
      kids(mm).forEach(function (ch) {
        if (hasClass(ch, 'bh-ms')) {
          var btn = firstWithClass(ch, 'bh-ms__btn');
          var body = firstWithClass(ch, 'bh-ms__body');
          if (!body) return;
          var id = (body.getAttribute('id') || '').replace(/^bh-ms-/, '');
          out.sections.push({ id: id, label: btn ? firstSpanText(btn) : '' });
          walk(body, id, '');
        } else if (hasClass(ch, 'bh-mu')) {
          kids(ch).forEach(function (a) { if (isTag(a, 'a') && hasClass(a, 'bh-mu__a')) push(a, 'utility', '', ''); });
        }
      });
      return out;
    }

    // data attribute on .bh -> ctx.words key (the Liquid writes one per key; the test checks they agree)
    var WORD_ATTRS = {
      returns: 'data-bh-w-returns', shipping: 'data-bh-w-shipping', contact: 'data-bh-w-contact', help: 'data-bh-w-help',
      verbs: 'data-bh-w-verbs', guide: 'data-bh-w-guide', products: 'data-bh-w-products', ignore: 'data-bh-w-ignore',
      veil: 'data-bh-w-veil'
    };

    return {
      LIMITS: LIMITS, EN_WORDS: EN_WORDS, WORD_ATTRS: WORD_ATTRS,
      fold: fold, expandQuery: expandQuery, pageQuery: pageQuery, parseTitle: parseTitle, sentenceCase: sentenceCase, withinOne: withinOne,
      parseList: parseList, readMenu: readMenu, buildModel: buildModel, statusLine: statusLine, pathOf: pathOf, isGuide: isGuide, guideNamer: guideNamer
    };
  })();
  window.BHSearch = BHSearch;

  /* -----------------------------------------------------------------------
     init: idempotent, safe to call again
     ----------------------------------------------------------------------- */
  function init() {
    var root = document.querySelector('[data-bh-header]');
    if (!root || root.__bhReady) return;
    root.__bhReady = true;

    var docEl = document.documentElement;
    var header = root.querySelector('[data-bh-bar]');
    var spacer = root.querySelector('.bh-spacer');
    var nav = root.querySelector('[data-bh-nav]');
    var scrollBox = root.querySelector('[data-bh-scroll]');
    var menuBtn = root.querySelector('[data-bh-menu-open]');
    var menuClose = root.querySelector('[data-bh-menu-close]');
    var searchWrap = root.querySelector('[data-bh-search]');
    var form = root.querySelector('[data-bh-form]');
    var input = root.querySelector('[data-bh-input]');
    var results = root.querySelector('[data-bh-results]');
    var searchClose = root.querySelector('[data-bh-search-close]');
    var searchBtn = root.querySelector('[data-bh-search-btn]');   // the desktop Search button (editorial and compact bar), a link to /search
    var searchOpeners = toArray(root.querySelectorAll('[data-bh-search-open]'));
    var ann = root.querySelector('[data-bh-ann]');
    // country / currency picker (step 4): desktop popover, mobile sheet, both optional
    var locTriggers = toArray(root.querySelectorAll('[data-bh-loc-open]'));
    var locPop = root.querySelector('[data-bh-loc-pop]');
    var locSheet = root.querySelector('[data-bh-loc-sheet]');
    var locSelects = toArray(root.querySelectorAll('[data-bh-loc-select]'));
    var locLangSelects = toArray(root.querySelectorAll('[data-bh-loc-lang-select]'));
    var locLangOnes = toArray(root.querySelectorAll('[data-bh-loc-lang-one]'));
    var locApplies = toArray(root.querySelectorAll('[data-bh-loc-apply]'));
    // Saved / Your Britten (S1): every one is optional; with the flags off none of them exists
    var savedToggles = toArray(root.querySelectorAll('[data-bh-saved-toggle]'));
    var savedPanel = root.querySelector('[data-bh-saved-panel]');
    var savedScreen = root.querySelector('[data-bh-saved-screen]');
    var menuRow = root.querySelector('.bh-menu-yb');
    // New mobile menu (bh--m2, settings.britten_mobile_menu_v2): every one is optional; with the setting off none exists
    var isM2 = root.classList.contains('bh--m2');
    var menuBag = root.querySelector('[data-bh-menu-bag]');
    var workshops = toArray(root.querySelectorAll('[data-bh-ws]'));

    if (!header || !spacer || !nav) {
      log('markup missing, header script idle');
      return;
    }

    var items = toArray(root.querySelectorAll('[data-bh-item]')).map(function (li) {
      return {
        li: li,
        link: li.querySelector('[data-bh-link]'),
        back: li.querySelector('[data-bh-back]')
      };
    }).filter(function (it) { return !!it.link; });

    var openIdx = -1;          // the open menu item (desktop panel or mobile section)
    var searchOpen = false;
    var menuOpen = false;
    var lastTrigger = null;    // where focus goes back to when search closes
    var hoverTimer = null;
    var lastPointer = 'mouse';
    var suppressFocus = false;
    var locOpen = false;
    var locTrigger = null;     // where focus goes back to when the picker closes
    var locTriggerQuiet = false;   // ... without scrolling to it (it is the footer select, say)
    var locInitial = '';       // the country that was selected when it opened
    var savedOpen = false;     // the Saved panel (desktop) or account screen (mobile) is showing
    var savedTrigger = null;   // where focus goes back to when it closes

    docEl.style.setProperty('--bh-offset', '0px');
    docEl.style.setProperty('--bh-mobile-bar', '0px');

    /* ---------------------------- state sync ---------------------------- */
    function syncState() {
      var desk = isDesktop();
      root.classList.toggle('bh--open', desk && (openIdx >= 0 || searchOpen || savedOpen));
      root.classList.toggle('bh--search', searchOpen);
      root.classList.toggle('bh--saved', savedOpen);
      root.classList.toggle('bh--menu', menuOpen);
      nav.classList.toggle('bh-nav--section', !desk && menuOpen && openIdx >= 0);
      nav.classList.toggle('bh-nav--saved', !desk && menuOpen && savedOpen);
      docEl.classList.toggle('bh-lock', !desk && (menuOpen || searchOpen));
      if (menuBtn) menuBtn.setAttribute('aria-expanded', menuOpen ? 'true' : 'false');
      if (savedPanel) savedPanel.hidden = !(savedOpen && desk);
      if (savedScreen) savedScreen.hidden = !(savedOpen && !desk);
      savedToggles.forEach(function (btn) { btn.setAttribute('aria-expanded', savedOpen ? 'true' : 'false'); });
      searchOpeners.forEach(function (btn) {
        if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', searchOpen ? 'true' : 'false');
      });
    }

    // The light veil behind a mega panel (bh--dim) is added only after the panel has been
    // open for DIM_AFTER_MS, so it does not flash while the pointer passes over the nav.
    // Moving from panel to panel before that restarts the wait; once it is on it stays
    // until no panel is open.
    var dimTimer = null;
    function cancelDim() {
      if (dimTimer) {
        clearTimeout(dimTimer);
        dimTimer = null;
      }
    }
    function syncDim() {
      cancelDim();
      if (openIdx < 0 || !isDesktop()) {
        root.classList.remove('bh--dim');
        return;
      }
      if (root.classList.contains('bh--dim')) return;
      dimTimer = setTimeout(function () {
        dimTimer = null;
        if (openIdx >= 0 && isDesktop()) root.classList.add('bh--dim');
      }, DIM_AFTER_MS);
    }

    function setOpen(i) {
      if (i === openIdx) return;
      if (openIdx >= 0) {
        items[openIdx].li.classList.remove('bh-item--open');
        items[openIdx].link.setAttribute('aria-expanded', 'false');
      }
      openIdx = i;
      if (i >= 0) {
        items[i].li.classList.add('bh-item--open');
        items[i].link.setAttribute('aria-expanded', 'true');
      }
      syncState();
      syncDim();
      requestScrollCheck();
    }

    /* ------------------------ desktop hover intent ---------------------- */
    function clearTimer() {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    }

    // Panel images are loading="lazy" so nothing downloads on page load. The moment
    // someone shows intent (hover or keyboard focus, before the 120ms open delay), the
    // images of THAT panel switch to eager, so they are not grey boxes when it opens.
    // Only when the panel's images are shown at this width, and only once per panel.
    function warmPanel(i) {
      var it = items[i];
      if (!it || it.warmed || !isDesktop()) return;
      var feats = it.li.querySelector('.bh-feats');
      if (!feats || window.getComputedStyle(feats).display === 'none') return;
      it.warmed = true;
      toArray(feats.querySelectorAll('img[loading="lazy"]')).forEach(function (img) {
        img.setAttribute('loading', 'eager');
      });
    }

    function openPanel(i) {
      if (searchOpen) closeSearch(false);
      if (locOpen) closeLoc(false);
      if (savedOpen) closeSaved(false);
      warmPanel(i);
      setOpen(i);
    }

    function enter(i, immediate) {
      clearTimer();
      if (!isDesktop()) return;
      warmPanel(i);
      var delay = (immediate || openIdx >= 0) ? 0 : HOVER_OPEN_MS;
      if (delay === 0) {
        openPanel(i);
        return;
      }
      hoverTimer = setTimeout(function () {
        hoverTimer = null;
        openPanel(i);
      }, delay);
    }

    function leave() {
      clearTimer();
      // the pointer has left: no veil may appear in the 200ms before the panel closes
      cancelDim();
      hoverTimer = setTimeout(function () {
        hoverTimer = null;
        if (isDesktop() && openIdx >= 0) setOpen(-1);
      }, HOVER_CLOSE_MS);
    }

    function closePanelNow() {
      clearTimer();
      if (openIdx >= 0) setOpen(-1);
    }

    function focusLink(i) {
      if (i < 0 || !items[i]) return;
      suppressFocus = true;
      items[i].link.focus();
      suppressFocus = false;
    }

    items.forEach(function (it, i) {
      it.link.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') enter(i, false);
      });
      it.link.addEventListener('focus', function () {
        if (suppressFocus || !isDesktop()) return;
        if (focusVisible(it.link)) enter(i, false);
      });
      it.link.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (!isDesktop()) {
          // mobile: the row opens its section screen instead of navigating
          e.preventDefault();
          openSection(i);
          return;
        }
        // touch at 1024px and up: first tap opens the panel, second follows the link
        if ((lastPointer === 'touch' || lastPointer === 'pen') && openIdx !== i) {
          e.preventDefault();
          clearTimer();
          openPanel(i);
        }
      });
      if (it.back) {
        it.back.addEventListener('click', function () {
          backToRoot();
        });
      }
    });

    header.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      clearTimer();
      // back inside before the panel closed: start the veil's wait again
      if (openIdx >= 0) syncDim();
    });
    header.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse' && isDesktop()) leave();
    });
    // the search / logo / icons cells close an open panel straight away
    toArray(root.querySelectorAll('.bh-left, .bh-logo, .bh-icons')).forEach(function (cell) {
      cell.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse' && isDesktop()) closePanelNow();
      });
    });
    header.addEventListener('focusout', function (e) {
      if (!isDesktop()) return;
      var to = e.relatedTarget;
      if (to && !header.contains(to)) {
        closePanelNow();
        if (searchOpen) closeSearch(false);
        if (savedOpen) closeSaved(false);
      }
    });

    document.addEventListener('pointerdown', function (e) {
      lastPointer = e.pointerType || 'mouse';
    }, true);
    document.addEventListener('keydown', function () {
      lastPointer = 'keyboard';
    }, true);

    // a click anywhere outside the header (the dimmed page included) closes things
    document.addEventListener('click', function (e) {
      if (!isDesktop()) return;
      // the picker's own popover and trigger are not "outside" it
      // a control outside the header opened it (footer, shipping bar...): the click that did so
      // is still on its way up to here, so ignore clicks for a moment after opening
      if (locOpen && Date.now() - locOpenedAt > 400 && !(locPop && locPop.contains(e.target)) && !locTriggers.some(function (t) { return t.contains(e.target); })) {
        closeLoc(false);
      }
      if (openIdx < 0 && !searchOpen && !savedOpen) return;
      if (header.contains(e.target) || savedToggles.some(function (t) { return t.contains(e.target); })) return;   // the announcement bar's greeting is a saved toggle outside <header>
      closePanelNow();
      if (searchOpen) closeSearch(false);
      // a click on something the page just removed (a button the panel re-drew) is not "outside"
      if (savedOpen && e.target.isConnected !== false) closeSaved(false);
    });

    // A link to a spot on THIS page (a #hash) does not unload the page, so a
    // full-screen mobile layer would stay open and keep the page locked. Close it.
    // On desktop, a click on any link inside an open panel or the search panel
    // closes that panel (the page may not change either).
    function sameDocHash(a) {
      return !!(a.hash && a.origin === window.location.origin && a.pathname === window.location.pathname);
    }
    function onLayerLinkClick(e) {
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      // the mobile top rows call preventDefault first (they open a section instead)
      if (!a || e.defaultPrevented) return;
      if (!isDesktop()) {
        if (!sameDocHash(a)) return;
        if (searchOpen) closeSearch(false);
        if (menuOpen) closeMenu(false, 'link');
        return;
      }
      if (a.closest('.bh-panel')) closePanelNow();
      else if (searchWrap && searchWrap.contains(a)) closeSearch(false);
    }
    nav.addEventListener('click', onLayerLinkClick);

    /* ------------------------------ search ------------------------------ */
    // One sheet for every width (search redesign v2, stage 2). Below 1024px it is the full-screen layer (top band, one
    // scroller); from 1024px it is the sheet under the header. The sheet's input is the ONE input on desktop (v3: the
    // bar holds a Search button, not a field): the button opens the sheet and focus moves to its input. Closing keeps the
    // text (phones clear it, as before).
    var suggestUrl = root.getAttribute('data-bh-suggest') || '';
    var searchUrl = root.getAttribute('data-bh-search-url') || '/search';
    // "See all results for '{{ q }}' ->": the placeholder reaches the browser intact
    var textSeeAll = root.getAttribute('data-bh-s-see-all') || '';
    var textNoMatch = root.getAttribute('data-bh-s-nomatch') || '';
    var textSearch = root.getAttribute('data-bh-s-label') || '';
    var currencyCode = root.getAttribute('data-bh-currency') || '';
    var groupText = {
      shop: root.getAttribute('data-bh-s-group-shop') || '',
      products: root.getAttribute('data-bh-s-group-products') || '',
      guides: root.getAttribute('data-bh-s-group-guides') || '',
      help: root.getAttribute('data-bh-s-group-help') || '',
      guideOne: root.getAttribute('data-bh-s-guide-one') || ''
    };
    var statusTpl = {
      productOne: root.getAttribute('data-bh-s-status-product-one') || '',
      productOther: root.getAttribute('data-bh-s-status-product-other') || '',
      collectionOne: root.getAttribute('data-bh-s-status-collection-one') || '',
      collectionOther: root.getAttribute('data-bh-s-status-collection-other') || '',
      helpOne: root.getAttribute('data-bh-s-status-help-one') || '',
      helpOther: root.getAttribute('data-bh-s-status-help-other') || ''
    };
    var debounceTimer = null;
    var controller = null;
    var requestId = 0;
    var searchDesk = true;                   // the sheet was opened at desktop width (phones clear the text on close)
    var cache = { sent: '', data: null };    // the last Shopify answer, by the query that was sent
    var resultsShown = false;                // results are showing (else the empty state)
    var asking = false;                      // text is in the box and its results are not drawn yet (debounce or request)
    var walk = [];                           // the results' arrow-key order (lead, products, shop, help, guides, See all)
    var searchBody = root.querySelector('.bh-search__body');
    var emptyEl = root.querySelector('[data-bh-empty]');
    var popularEl = root.querySelector('[data-bh-popular]');   // absent when the section has no Popular searches
    var statusEl = root.querySelector('[data-bh-status]');
    var clearBtn = root.querySelector('[data-bh-search-clear]');
    var searchX = root.querySelector('[data-bh-search-x]');

    // Where Enter, the Popular links without a path and See all go: the results page, products only (type=product, so the
    // page lists products and not the pages and articles Shopify would add to "everything")
    function goSearch(term) {
      window.location.href = searchUrl + (searchUrl.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(term) + '&type=product';
    }

    function setPlaceholder() {
      if (!input) return;
      var text = root.getAttribute(isDesktop() ? 'data-bh-s-ph-desktop' : 'data-bh-s-ph-mobile');
      if (text) input.setAttribute('placeholder', text);
    }

    // Phones: the sheet is a dialog (role, aria-modal, name). Desktop search is not modal, so none of the three there.
    function syncSearchRole() {
      if (!searchWrap) return;
      if (isDesktop()) {
        searchWrap.removeAttribute('role');
        searchWrap.removeAttribute('aria-modal');
        searchWrap.removeAttribute('aria-label');
      } else {
        searchWrap.setAttribute('role', 'dialog');
        searchWrap.setAttribute('aria-modal', 'true');
        if (textSearch) searchWrap.setAttribute('aria-label', textSearch);
      }
    }

    // Phones: the layer covers the WHOLE layout viewport at all times (CSS: top and bottom 0, never shorter than 100lvh), so
    // the page can never show through behind the keyboard or Safari's translucent accessory bar. It is NOT sized to the
    // visual viewport. While it is open two numbers are written to it instead:
    //   --bh-kb  how much of the layer's bottom the visual viewport does not reach (the keyboard, the accessory bar, a
    //            toolbar): the ONE scroller takes it as extra padding-bottom, so the last row scrolls clear of it;
    //   --bh-kt  how far iOS has moved the visual viewport down inside the layout viewport: the layer takes it as
    //            padding-top, so the top band stays at the top of what is on screen, never under the notch or off it.
    // The top band stays in landscape.
    function unfitViewport() {
      if (!searchWrap) return;
      searchWrap.style.removeProperty('--bh-kb');
      searchWrap.style.removeProperty('--bh-kt');
    }
    function fitViewport() {
      var vv = window.visualViewport;
      if (!searchWrap || !searchOpen || isDesktop() || !vv) {
        unfitViewport();
        return;
      }
      var kt = Math.max(0, Math.round(vv.offsetTop));
      var kb = Math.max(0, Math.round(searchWrap.offsetHeight - (vv.height + vv.offsetTop)));
      searchWrap.style.setProperty('--bh-kt', kt + 'px');
      searchWrap.style.setProperty('--bh-kb', kb + 'px');
    }
    if (window.visualViewport && window.visualViewport.addEventListener) {
      window.visualViewport.addEventListener('resize', fitViewport);
      window.visualViewport.addEventListener('scroll', fitViewport);
    }

    function cancelRequest() {
      clearTimeout(debounceTimer);
      asking = false;
      requestId++;
      if (controller) {
        try { controller.abort(); } catch (e) { /* already done */ }
        controller = null;
      }
    }

    function syncClear() {
      if (clearBtn && input) clearBtn.hidden = !input.value;
    }

    // Back to the empty state: Popular, Start here, Your Britten (phones), Bestsellers.
    function showEmpty() {
      resultsShown = false;
      asking = false;
      walk = [];
      if (results) {
        results.textContent = '';
        results.hidden = true;
      }
      if (emptyEl) emptyEl.hidden = false;
      if (statusEl) statusEl.textContent = '';
      if (searchWrap) searchWrap.classList.remove('bh-search--busy');
    }

    // Phones: the price as Shopify gives it (a plain number in the shop / market currency), set in that currency when the
    // browser can; anything else is shown exactly as given. Desktop uses it too since the redesign.
    function priceText(raw) {
      if (raw == null || raw === '') return '';
      var text = String(raw);
      var n = parseFloat(text);
      if (currencyCode && isFinite(n) && /^[0-9.,\s]+$/.test(text) && window.Intl && Intl.NumberFormat) {
        try {
          var whole = Math.round(n) === n;
          return new Intl.NumberFormat(docEl.lang || undefined, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: whole ? 0 : 2
          }).format(n);
        } catch (e) { /* unknown code: show it as given */ }
      }
      return text;
    }

    // The search logic is BHSearch (above, no DOM): aliases, name parse, lead, intent, shop / help / guide rows,
    // limits. This file only builds its ctx from the page and draws its model. ctx is built once, here, from the
    // drawer (the menu model, which also gives the product words and the help rows, found by href), the
    // locale's intent words (data-bh-w-*, locale keys britten.header.search_words_*) and the two help sub-lines.
    var searchCtx = null;
    function buildSearchCtx() {
      var words = {};
      Object.keys(BHSearch.WORD_ATTRS).forEach(function (k) {
        words[k] = BHSearch.parseList(root.getAttribute(BHSearch.WORD_ATTRS[k]) || '');
      });
      return {
        lang: (docEl.getAttribute('lang') || 'en').toLowerCase().slice(0, 2),
        searchUrl: searchUrl,
        menu: BHSearch.readMenu(nav.querySelector('[data-bh-mm]')),
        words: words,
        subs: {
          returns: root.getAttribute('data-bh-s-help-returns-sub') || '',
          shipping: root.getAttribute('data-bh-s-help-shipping-sub') || ''
        }
      };
    }
    searchCtx = buildSearchCtx();

    // Start here: the empty state's two links, read once. The results draw them again beside the products (desktop).
    var startEl = root.querySelector('[data-bh-start]');
    var startHead = '';
    var startRows = [];
    if (startEl) {
      var startH = startEl.querySelector('.bh-sg__h');
      startHead = startH ? startH.textContent : '';
      startRows = toArray(startEl.querySelectorAll('a.bh-sx')).map(function (a, i) {
        var t = a.querySelector('.bh-sx__t');
        var s = a.querySelector('.bh-sx__s');
        return { href: a.getAttribute('href') || '', label: t ? t.textContent : '', sub: s ? s.textContent : '', first: i === 0, sample: a.classList.contains('bh-sx--sample') };
      });
    }
    var sampleRow = startRows.filter(function (r) { return r.sample; })[0] || null;

    // Bestsellers: the Liquid prints the raw titles; the name goes first, the style (desktop only) under it, and the image
    // is named after the product.
    toArray(root.querySelectorAll('[data-bh-best-title]')).forEach(function (el) {
      var parsed = BHSearch.parseTitle(el.textContent);
      var link = el.parentNode;
      el.textContent = parsed.name;
      if (parsed.style) link.insertBefore(make('span', 'bh-best__s', parsed.style), el.nextSibling);
      var img = link.querySelector('img');
      if (img) img.setAttribute('alt', ''); // the name is the link's own text; a named image would read it twice
    });

    // The workshop's live line, for the Contact help row: the drawer's own Contact row is kept current by updateWorkshop()
    // (run when search opens); this copies what it says.
    function workshopLine() {
      var el = workshops[0];
      var out = el ? el.querySelector('[data-bh-ws-text]') : null;
      return { text: out ? out.textContent : '', state: el ? (el.getAttribute('data-bh-ws-state') || '') : '' };
    }

    /* ---- the model -> DOM. Nothing is filtered here; every row the model holds is drawn. -------------------------- */
    function href(u) { return safeUrl(u); }

    function section(kind, label) {
      var sec = make('section', 'bh-sg bh-sg--' + kind);
      var h = make('p', 'bh-sg__h', label);
      h.id = 'bh-rg-' + kind + '-h';
      sec.appendChild(h);
      return sec;
    }
    // the group's list is named by its label
    function listOf(sec, kind, cls) {
      var ul = make('ul', cls || 'bh-sg__list');
      ul.setAttribute('aria-labelledby', 'bh-rg-' + kind + '-h');
      sec.appendChild(ul);
      return ul;
    }
    function item(ul, node) {
      var li = make('li');
      li.appendChild(node);
      ul.appendChild(li);
    }
    // tiles at 1024-1279px: each group takes as many shares of the row as it has tiles (see the CSS)
    function shares(sec, n) { sec.style.setProperty('--n', String(n)); }
    function track(a, kind, pos) {
      a.setAttribute('data-bh-k', kind);
      a.setAttribute('data-bh-pos', String(pos));
      return a;
    }

    // A row with a title and a muted line: Start here, shop and help rows. kind: start | shop | help
    function row(r, kind, pos, extra) {
      var a = make('a', 'bh-sx bh-sx--' + kind + (extra ? ' ' + extra : ''));
      a.setAttribute('href', href(r.href));
      if (pos) track(a, kind, pos);
      var text = a;
      if (kind === 'help') {
        text = make('span', 'bh-sx__x');
        a.appendChild(text);
      }
      text.appendChild(make('span', 'bh-sx__t', r.label));
      var sub = r.sub || '';
      if (kind === 'help' && r.live) {
        // Contact: the live workshop line with its dot (the dot shows when the state is open)
        var ws = workshopLine();
        sub = ws.text || sub;
        if (ws.state) a.setAttribute('data-bh-ws-state', ws.state);
        if (sub) {
          var live = make('span', 'bh-sx__s bh-sx__s--live');
          var dot = make('span', 'bh-ml__dot');
          dot.setAttribute('aria-hidden', 'true');
          live.appendChild(dot);
          live.appendChild(make('span', null, sub));
          text.appendChild(live);
        }
      } else if (kind === 'shop') {
        // 1024-1279px: the tile reads "Shop . Veils . Veil lengths"; elsewhere the sub-line is just "Veils . Veil lengths"
        if (sub) {
          var s = make('span', 'bh-sx__s');
          s.appendChild(make('span', 'bh-sx__pre', groupText.shop + ' · '));
          s.appendChild(document.createTextNode(sub));
          text.appendChild(s);
        } else {
          text.appendChild(make('span', 'bh-sx__s bh-sx__s--tile', groupText.shop));
        }
      } else if (sub) {
        text.appendChild(make('span', 'bh-sx__s', sub));
      }
      if (kind === 'help') {
        var arrow = make('span', 'bh-sx__arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        a.appendChild(arrow);
      }
      return a;
    }

    // A product: lead, a row or a square (the CSS decides which). px is the slot it fills; the image asks for 2x.
    function productLink(p, kind, pos, px, eager) {
      var a = track(make('a', 'bh-sr' + (kind === 'lead' ? ' bh-sr--lead' : '')), kind, pos);
      a.setAttribute('href', href(p.url));
      var box = make('span', 'bh-sr__img');
      var src = sized(p.image, px * 2);
      if (src) {
        var img = make('img');
        img.setAttribute('src', src);
        img.setAttribute('alt', ''); // decorative: the name is in the row's text
        img.setAttribute('width', String(px));
        img.setAttribute('height', String(px));
        img.setAttribute('loading', eager ? 'eager' : 'lazy');
        img.setAttribute('decoding', 'async');
        box.appendChild(img);
      }
      a.appendChild(box);
      var body = make('span', 'bh-sr__b');
      body.appendChild(make('span', 'bh-sr__t', p.name || p.title || ''));
      if (p.style) body.appendChild(make('span', 'bh-sr__s', p.style));
      var price = priceText(p.price);
      if (price) body.appendChild(make('span', 'bh-sr__p', price));
      a.appendChild(body);
      return a;
    }

    function renderResults(query, data) {
      var model = BHSearch.buildModel(data, query, searchCtx);
      var desk = isDesktop();
      var none = model.noMatch;
      var service = model.intent === 'service' && model.help.length > 0;
      // --service: help rows only; --none: no match; --cols: everything else (the left column / tile row, then the products)
      var rs = make('div', 'bh-rs ' + (none ? 'bh-rs--none' : (service ? 'bh-rs--service' : 'bh-rs--cols')));
      var sec, ul, a;
      var leadA = null, prodA = [], shopA = [], helpA = [], guideA = [], popA = [];
      var left = false;                    // anything in the desktop left column (or the 1024-1279px tile row)

      if (none) {
        rs.appendChild(make('p', 'bh-none', textNoMatch));
        if (popularEl) {
          var pop = popularEl.cloneNode(true);
          var ph = pop.querySelector('.bh-sg__h');
          var pl = pop.querySelector('ul');
          pop.removeAttribute('data-bh-popular');
          if (ph) ph.id = 'bh-rg-pop-h';
          if (pl) pl.setAttribute('aria-labelledby', 'bh-rg-pop-h');
          popA = toArray(pop.querySelectorAll('a'));
          rs.appendChild(pop);
        }
      }

      // Shop: the matching menu links, then Shopify collections
      if (model.shop.length) {
        sec = section('shop', groupText.shop);
        shares(sec, model.shop.length);
        ul = listOf(sec, 'shop');
        model.shop.forEach(function (r, i) {
          a = row(r, 'shop', i + 1);
          shopA.push(a);
          item(ul, a);
        });
        rs.appendChild(sec);
        left = true;
      }

      // Help: a service question's rows, the one row beside a product word, or Contact when nothing matched
      var helpRows = none ? model.noMatchHelp : model.help;
      if (helpRows.length) {
        sec = section('help', groupText.help);
        shares(sec, helpRows.length);
        ul = listOf(sec, 'help');
        helpRows.forEach(function (r, i) {
          a = row(r, 'help', i + 1);
          helpA.push(a);
          item(ul, a);
        });
        rs.appendChild(sec);
        left = left || (!service && !none);
      }

      // Products: the lead (the veil she named), then the rest. Desktop with no lead: six squares; otherwise rows.
      var lead = model.lead;
      var productsSec = null;
      if (lead || model.products.length) {
        sec = section('products', groupText.products);
        productsSec = sec;
        if (lead) sec.className += ' bh-sg--lead';
        var wrap = make('div', 'bh-sp');
        if (lead) {
          leadA = productLink(lead, 'lead', 1, desk ? 260 : 128, true);
          wrap.appendChild(leadA);
        }
        if (model.products.length) {
          ul = listOf(sec, 'products', 'bh-sp__list');
          model.products.forEach(function (p, i) {
            var px = (desk && !lead) ? 165 : (desk ? 72 : 88);
            a = productLink(p, 'product', i + 1, px, desk || i < 3);
            prodA.push(a);
            item(ul, a);
          });
          wrap.appendChild(ul);
        }
        sec.appendChild(wrap);
        rs.appendChild(sec);
      }

      // Guides
      if (model.guides.length) {
        sec = section('guides', groupText.guides);
        shares(sec, model.guides.length);
        ul = listOf(sec, 'guides');
        model.guides.forEach(function (g, i) {
          a = track(make('a', 'bh-gl'), 'guide', i + 1);
          a.setAttribute('href', href(g.href));
          a.appendChild(make('span', 'bh-gl__t', g.label));
          a.appendChild(make('span', 'bh-gl__s', groupText.guideOne));
          guideA.push(a);
          item(ul, a);
        });
        rs.appendChild(sec);
        left = true;
      }

      // Desktop left column / tile row: Start here when no menu match, else the Order samples tile unless the match IS
      // the samples link (artboards D3, D4, D8). A Start link that is already a row or a guide here is not drawn twice.
      // Phones show neither.
      if (!service && !none && startRows.length) {
        var shown = {};
        model.shop.concat(model.help, model.guides).forEach(function (r) { shown[BHSearch.pathOf(r.href)] = true; });
        var startShow = startRows.filter(function (r) { return !shown[BHSearch.pathOf(r.href)]; });
        if (!model.shop.length) {
          if (startShow.length) {
            sec = section('start', startHead);
            sec.className += ' bh-sg--d';
            shares(sec, startShow.length);
            ul = listOf(sec, 'start');
            startShow.forEach(function (r) {
              item(ul, row(r, 'start', 0, (r.first ? 'bh-sx--first' : '') + (r.sample ? ' bh-sx--sample' : '')));
            });
            rs.appendChild(sec);
            left = true;
          }
        } else if (sampleRow && !shown[BHSearch.pathOf(sampleRow.href)]) {
          sec = make('div', 'bh-sg bh-sg--samples bh-sg--d');
          shares(sec, 1);
          sec.appendChild(row(sampleRow, 'start', 0, 'bh-sx--sample'));
          rs.appendChild(sec);
        }
      }
      if (left) {
        var rule = make('span', 'bh-rs__rule');
        rule.setAttribute('aria-hidden', 'true');
        rs.appendChild(rule);
      }

      // See all, always
      var all = make('a', 'bh-rs__all', textSeeAll.replace(/\{\{\s*q\s*\}\}/g, function () { return model.seeAll.query; }));
      all.setAttribute('href', model.seeAll.href);
      all.setAttribute('data-bh-q', BHSearch.pageQuery(model.seeAll.query));   // what goSearch sends: the alias if one fires, else her words as typed
      track(all, 'see_all', 1);
      // desktop: under the products, in the right-hand column (the left column may be taller); phones: last, after Guides
      if (desk && productsSec) productsSec.appendChild(all);
      else rs.appendChild(all);

      // The arrow-key order is explicit (the left column comes first on desktop, so the DOM order is not the walk)
      walk = [].concat(popA, leadA ? [leadA] : [], prodA, shopA, helpA, guideA, [all]);

      results.textContent = '';
      results.appendChild(rs);
      results.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      resultsShown = true;
      asking = false;
      if (searchBody) searchBody.scrollTop = 0;
      if (searchWrap) searchWrap.scrollTop = 0;
      // announce counts, not the list
      if (statusEl) statusEl.textContent = none ? textNoMatch : BHSearch.statusLine(model.counts, statusTpl);
    }

    function fetchSuggest(query, sent) {
      if (!window.fetch || !suggestUrl) {
        asking = false;
        return;
      }
      cancelRequest();
      asking = true;
      var mine = requestId;
      controller = window.AbortController ? new AbortController() : null;
      var url = suggestUrl + '.json?q=' + encodeURIComponent(sent) +
        '&resources[type]=product,collection,page&resources[limit]=' + SUGGEST_LIMIT +
        '&resources[limit_scope]=each';
      var opts = { credentials: 'same-origin', headers: { 'Accept': 'application/json' } };
      if (controller) opts.signal = controller.signal;
      searchWrap.classList.add('bh-search--busy');
      fetch(url, opts)
        .then(function (r) {
          if (!r.ok) throw new Error('suggest ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (mine !== requestId) return;
          cache = { sent: sent, data: data };
          renderResults(query, data);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          log('suggest failed: ' + err);
          if (mine === requestId) showEmpty();
        })
        .then(function () {
          if (mine === requestId) searchWrap.classList.remove('bh-search--busy');
        });
    }

    // Results for this text now: the last answer if it was for the same sent query (a reopen, a backspace), else ask.
    function showFor(query) {
      var sent = BHSearch.expandQuery(query);
      if (cache.data && cache.sent === sent) {
        cancelRequest();
        searchWrap.classList.remove('bh-search--busy');
        renderResults(query, cache.data);
        return;
      }
      fetchSuggest(query, sent);
    }

    // Typing, pasting, IME: the sheet's input has changed. 200ms debounce, the previous request aborted.
    function onSearchInput() {
      clearTimeout(debounceTimer);
      var query = input.value.trim();
      if (query.length < SUGGEST_MIN_CHARS) {
        cancelRequest();
        showEmpty();
        return;
      }
      asking = true;
      debounceTimer = setTimeout(function () { showFor(query); }, SUGGEST_DEBOUNCE_MS);
    }

    // trigger: the button that opened it (null from api.openSearch). The sheet's input takes focus, caret at the end.
    function openSearch(trigger) {
      if (!searchWrap || !input) return;
      if (searchOpen) {
        input.focus();
        return;
      }
      lastTrigger = trigger || null;
      searchDesk = isDesktop();
      clearTimer();
      if (locOpen) closeLoc(false);
      if (savedOpen && isDesktop()) closeSaved(false);
      if (openIdx >= 0 && isDesktop()) setOpen(-1);
      searchOpen = true;
      // a phone search opened from the drawer keeps source: menu (the drawer's own DOM still holds the button)
      bhTrack('search_open', { source: (trigger && nav.contains(trigger)) ? 'menu' : 'button', handler: 'theme' });
      setPlaceholder();
      // the text kept from last time is still in the input (desktop; phones cleared it on close)
      syncClear();
      updateWorkshop();
      syncState();
      // focus inside the click handler, synchronously, so iOS raises the keyboard; caret at the end
      input.focus();
      try {
        var end = input.value.length;
        input.setSelectionRange(end, end);
      } catch (e) { /* a type that has no selection */ }
      fitViewport();
      var query = input.value.trim();
      if (query.length >= SUGGEST_MIN_CHARS) showFor(query);
      else showEmpty();
      requestScrollCheck();
    }

    // returnFocus: Escape, Close and Search again. The input keeps its text on desktop; phones clear it, as before.
    function closeSearch(returnFocus) {
      if (!searchOpen) return;
      searchOpen = false;
      cancelRequest();
      if (!searchDesk) {
        input.value = '';
        syncClear();
      }
      unfitViewport();
      syncState();
      requestScrollCheck();
      if (returnFocus) {
        // desktop: the Search button (one element, editorial and compact bar); phones: the button she used
        var target = null;
        if (searchDesk && searchBtn && isShown(searchBtn)) target = searchBtn;
        if (!target) target = (lastTrigger && isShown(lastTrigger)) ? lastTrigger : searchOpeners.filter(isShown)[0];
        if (target) target.focus();
      }
      lastTrigger = null;
    }

    // The /search page has its own input ([data-bh-page-input], rendered by sections/britten-search.liquid). There the sheet
    // never opens: every search opener (the Search button, the phone magnifier, the drawer's entries) moves focus to that
    // input, selects its text and scrolls to the top. No such input (the old results page, the setting off): null, and
    // the opener opens the sheet as it does everywhere else.
    function searchPageInput() {
      if (pageType() !== 'search') return null;
      var el = document.querySelector('[data-bh-page-input]');
      return (el && isShown(el)) ? el : null;
    }
    function focusPageInput(el) {
      // inside the click handler, so iOS raises the keyboard
      el.focus();
      try { el.select(); } catch (e) { /* a type that has no selection */ }
      window.scrollTo(0, 0);
    }

    // One path for a click, Enter or Space on any opener. btn is null from api.openSearch.
    function activateSearch(btn) {
      // Search again closes (desktop; the phone magnifier is under the layer, so it cannot be pressed while open)
      if (btn && searchOpen && isDesktop() && btn.hasAttribute('aria-expanded')) {
        closeSearch(true);
        return;
      }
      var pageInput = searchPageInput();
      // Opened from the drawer on a phone: the drawer closes first (and its history entry goes), then our layer opens
      // or, on the /search page, focus goes to the page's input
      var droppedEntry = false;
      if (menuOpen && !isDesktop()) {
        closeMenu(false, 'search');
        dropMenuEntry(false);
        droppedEntry = true;
      }
      if (pageInput) {
        focusPageInput(pageInput);
        // Back off our history entry restores the old scroll position once it has settled: scroll to the top again after it
        if (droppedEntry) {
          window.addEventListener('popstate', function () {
            window.requestAnimationFrame(function () { window.scrollTo(0, 0); });
          }, { once: true });
        }
        return;
      }
      openSearch(btn);
    }

    api.openSearch = function () { activateSearch(null); };
    api.closeSearch = function () { closeSearch(false); };

    searchOpeners.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        // the Search button is a link to /search: a modified click (a new tab, a new window) is the browser's own
        if (btn.tagName === 'A' && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
        e.preventDefault();
        activateSearch(btn);
      });
    });
    // Space: a link with role=button does not click on Space (a real button does), so the script does
    if (searchBtn) {
      searchBtn.addEventListener('keydown', function (e) {
        if ((e.key !== ' ' && e.key !== 'Spacebar') || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
        e.preventDefault();   // the page must not scroll
        searchBtn.click();
      });
    }
    if (searchClose) searchClose.addEventListener('click', function () { closeSearch(true); });
    if (searchX) searchX.addEventListener('click', function () { closeSearch(true); });
    if (clearBtn) {
      // empties the input, shows the empty state, keeps focus in the input
      clearBtn.addEventListener('click', function () {
        input.value = '';
        syncClear();
        cancelRequest();
        showEmpty();
        input.focus();
      });
    }

    if (input) {
      input.addEventListener('input', function () {
        syncClear();
        onSearchInput();
      });
    }

    // Arrow keys walk an explicit list, never the DOM: results = lead, products, shop, help, guides, See all; empty state =
    // Popular, then Start here. A hidden element is never focused, and the key is only taken when focus really moved.
    // While text is in the box and its results are still on the way (the debounce, the request) there is no list to walk:
    // the empty state is still on screen under the text, and walking it would jump into Popular. The arrows do nothing.
    function arrowItems() {
      if (asking) return [];
      var list = resultsShown ? walk : (emptyEl ? toArray(emptyEl.querySelectorAll('.bh-pop__a, [data-bh-start] .bh-sx')) : []);
      return list.filter(isShown);
    }
    function moveFocus(e, to) {
      if (!to) return;
      to.focus();
      if (document.activeElement === to) e.preventDefault();
    }
    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') moveFocus(e, arrowItems()[0]);
      });
    }
    if (searchWrap) {
      searchWrap.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var list = arrowItems();
        var at = list.indexOf(e.target);
        if (at < 0) return;
        if (e.key === 'ArrowDown') moveFocus(e, list[at + 1]);
        else moveFocus(e, at === 0 ? input : list[at - 1]);
      });
      searchWrap.addEventListener('click', function (e) {
        // analytics: search_click { kind: lead | product | shop | help | guide | see_all, position (1-based inside its kind) }
        var k = e.target.closest ? e.target.closest('[data-bh-k]') : null;
        if (k) bhTrack('search_click', { kind: k.getAttribute('data-bh-k'), position: parseInt(k.getAttribute('data-bh-pos'), 10) || 1 });
        // Popular links with no path and "See all results" carry data-bh-q. They keep a /search?q= href (works without
        // script) but go where Enter goes, so every route lands on the same results page. A link with a path is plain.
        var t = e.target.closest ? e.target.closest('[data-bh-q]') : null;
        var plain = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        if (t && plain) {
          var term = t.getAttribute('data-bh-q');
          if (term) {
            e.preventDefault();
            goSearch(term);
            return;
          }
        }
        onLayerLinkClick(e);
      });
    }
    // Enter in the sheet: an empty box is blocked and refocused; otherwise the form goes to the results page with the
    // ALIASED query (BHSearch.expandQuery, what predictive search already sends, so "isabella" lands on real results) and
    // type=product. The hidden type field in the form does the same job with no script.
    if (form && input) {
      form.addEventListener('submit', function (e) {
        var typed = input.value.trim();
        e.preventDefault();
        if (!typed) {
          input.focus();
          return;
        }
        goSearch(BHSearch.pageQuery(typed));
      });
    }

    /* ------------------- country / currency picker (step 4) ------------------ */
    // The picker is a UI shell. The country list and the submit belong to the store's
    // ONE localization form, the footer's (#FooterCountryForm), which the shipping bar,
    // the delivery estimate and the cart delivery box also drive:
    //  - on open, the options are copied from the footer's <select name="country_code">
    //    (one source for the list and labels, including the EU = BE and Rest of World = SG
    //    stand-ins, and which one is selected);
    //  - when a country is chosen, country_code is set on THAT form, the stale currency_code / locale_code
    //    hidden fields are removed (so the new market's own currency and language apply)
    //    and it is submitted.
    // This header holds no <form> and no name="country_code" of its own, so none of those
    // scripts can ever grab something in here as "the first localization form".
    // Native language names for the language row (the markets file holds codes only).
    var LOC_LANG_NAMES = {
      en: 'English', de: 'Deutsch', fr: 'Français', es: 'Español',
      it: 'Italiano', nl: 'Nederlands', pl: 'Polski'
    };
    var locMarkets = null;       // assets/britten-markets.json, once loaded (country -> languages)
    var locMarketsPromise = null;
    var locServerLangs = [];     // the language codes this market offers (server-rendered select)
    var locCurrentLang = (docEl.getAttribute('lang') || '').toLowerCase();
    var locBusy = false;
    var locMarketsFailed = false;   // the markets file could not be read
    var locOpenedAt = 0;            // when another control (not the pill) opened the panel

    function localizationForm() {
      return document.getElementById('FooterCountryForm') || document.querySelector('form[action$="/localization"]');
    }

    function loadLocMarkets() {
      if (locMarketsPromise) return locMarketsPromise;
      var url = root.getAttribute('data-bh-markets-url');
      if (!url || !window.fetch) {
        locMarketsFailed = true;
        locMarketsPromise = Promise.resolve(null);
        return locMarketsPromise;
      }
      locMarketsPromise = window.fetch(url, { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (data) { locMarkets = data; return data; })
        .catch(function () { locMarketsFailed = true; return null; });
      return locMarketsPromise;
    }

    function fillLocOptions() {
      var form = localizationForm();
      var source = form && form.querySelector('select[name="country_code"]');
      if (source && source.options.length) {
        // the country the page is in: the footer option that was rendered selected, not
        // whatever value the footer select holds now (a visitor may have changed it there)
        // the country the page is in comes from Liquid (data-bh-country); the footer's rendered
        // selection is only the fallback
        var own = root.getAttribute('data-bh-country') || '';
        var current = toArray(source.options).filter(function (o) { return o.value === own; })[0] ||
          toArray(source.options).filter(function (o) { return o.defaultSelected; })[0];
        var currentValue = current ? current.value : source.value;
        locSelects.forEach(function (sel) {
          sel.textContent = '';
          toArray(source.options).forEach(function (o) {
            var opt = document.createElement('option');
            opt.value = o.value;
            opt.textContent = (o.textContent || '').replace(/\s+/g, ' ').trim();
            sel.appendChild(opt);
          });
          sel.value = currentValue;
        });
      }
      // with no source the single server-rendered option (the current country) stays
      locInitial = locSelects.length ? locSelects[0].value : '';
    }

    // The languages a country offers: this market's own list for the current country (known
    // from Liquid, no fetch), else the markets file; a country missing from it has one
    // language, English. null = the file has not arrived yet: the row shows nothing until it
    // does (it is re-rendered then), so it never claims "one language" for a country that has two.
    function langsFor(code) {
      if (code === locInitial && locServerLangs.length) return locServerLangs;
      if (!locMarkets && !locMarketsFailed) return null;
      var entry = locMarkets && locMarkets.countries && locMarkets.countries[code];
      if (entry && entry.languages && entry.languages.length) return entry.languages;
      return [(locMarkets && locMarkets.default_language) || 'en'];
    }

    function langName(code) {
      return LOC_LANG_NAMES[code] || String(code).toUpperCase();
    }

    // The "Change language" row for the country now chosen: a select when it offers more than
    // one language, else the sentence naming the single one. Both layers are kept alike.
    function renderLang(code) {
      var langs = langsFor(code);
      var langWraps = toArray(root.querySelectorAll('[data-bh-loc-lang-wrap]'));   // the sheet's whole "Change language" block
      if (!langs) {
        langWraps.forEach(function (w) { w.hidden = true; });
        locLangSelects.forEach(function (sel) { sel.hidden = true; });
        locLangOnes.forEach(function (p) { p.hidden = true; });
        return;
      }
      var many = langs.length > 1;
      langWraps.forEach(function (w) { w.hidden = !many; });
      var keep = code === locInitial ? locCurrentLang : '';
      locLangSelects.forEach(function (sel) {
        if (many) {
          sel.textContent = '';
          langs.forEach(function (l) {
            var opt = document.createElement('option');
            opt.value = l;
            opt.textContent = langName(l);
            sel.appendChild(opt);
          });
          sel.value = langs.indexOf(keep) >= 0 ? keep : langs[0];
        }
        sel.hidden = !many;
      });
      locLangOnes.forEach(function (p) {
        var tpl = p.getAttribute('data-bh-tpl');
        if (tpl && !many) p.textContent = tpl.replace('[[language]]', langName(langs[0]));
        p.hidden = many;
      });
    }

    function activeLayer() {
      return isDesktop() ? locPop : locSheet;
    }

    function layerPart(layer, selector) {
      return layer ? layer.querySelector(selector) : null;
    }

    function syncLoc() {
      root.classList.toggle('bh--loc', locOpen && isDesktop());
      locTriggers.forEach(function (t) { t.setAttribute('aria-expanded', locOpen ? 'true' : 'false'); });
    }

    // Keep the popover inside the viewport, 16px from each edge, wherever its trigger is
    // (editorial header, or the compact bar, both at the left). It is shifted with a transform so
    // the CSS anchor stays as designed; the shift is measured fresh each time.
    function clampLoc() {
      if (!locOpen || !locPop || locPop.hidden || !isDesktop()) return;
      locPop.style.transform = '';
      var r = locPop.getBoundingClientRect();
      var vw = document.documentElement.clientWidth || window.innerWidth;
      var dx = 0;
      if (r.right > vw - LOC_GUTTER) dx = (vw - LOC_GUTTER) - r.right;
      if (r.left + dx < LOC_GUTTER) dx = LOC_GUTTER - r.left;
      if (dx) locPop.style.transform = 'translateX(' + Math.round(dx) + 'px)';
      // the caret points at the header pill, wherever the panel ended up
      var pill = root.querySelector('.bh-locwrap [data-bh-loc-open]');
      if (pill && isShown(pill)) {
        var pr = pill.getBoundingClientRect();
        var pb = locPop.getBoundingClientRect();
        var cx = pr.left + pr.width / 2 - pb.left;
        cx = Math.max(20, Math.min(pb.width - 20, cx));
        locPop.style.setProperty('--bh-caret-x', Math.round(cx) + 'px');
      }
    }

    // trigger: the button that opened it. preset: a country code to show chosen (the footer and
    // the other selectors). origin: the control that asked, so focus goes back to it, not to
    // the header pill (which may be scrolled out of sight).
    function openLoc(trigger, preset, origin) {
      if (locOpen) {
        closeLoc(true);
        return;
      }
      var layer = activeLayer();
      if (!layer) return;
      clearTimer();
      if (searchOpen) closeSearch(false);
      if (savedOpen && isDesktop()) closeSaved(false);
      if (openIdx >= 0 && isDesktop()) setOpen(-1);
      fillLocOptions();
      if (preset) {
        locSelects.forEach(function (sel) {
          if (sel.querySelector('option[value="' + preset + '"]')) sel.value = preset;
        });
      }
      var chosen = layerPart(layer, '[data-bh-loc-select]');
      renderLang(chosen ? chosen.value : locInitial);
      loadLocMarkets().then(function () {
        var now = layerPart(activeLayer(), '[data-bh-loc-select]');
        if (locOpen && now) renderLang(now.value);
      });
      resetLocNote();
      locOpen = true;
      locTrigger = origin || trigger || null;
      locTriggerQuiet = !!origin;
      layer.hidden = false;
      syncLoc();
      clampLoc();
      var first = layer.querySelector('select');
      if (first) first.focus();
      requestScrollCheck();
    }

    function closeLoc(returnFocus) {
      if (!locOpen) return;
      locOpen = false;
      if (locPop) {
        locPop.hidden = true;
        locPop.style.transform = '';
      }
      if (locSheet) locSheet.hidden = true;
      resetLocNote();
      syncLoc();
      if (returnFocus && locTrigger && isShown(locTrigger)) {
        if (locTriggerQuiet) locTrigger.focus({ preventScroll: true });
        else locTrigger.focus();
      }
      locTrigger = null;
      locTriggerQuiet = false;
      requestScrollCheck();
    }

    // locale: the language chosen in the panel's language select, or null when there is none to
    // choose (a clean country switch: the new market's own language applies).
    function submitCountry(code, locale) {
      var form = localizationForm();
      if (form) {
        // the new market's own currency applies, and its language unless one was chosen
        ['currency_code', 'locale_code'].forEach(function (name) {
          toArray(form.querySelectorAll('input[name="' + name + '"]')).forEach(function (n) {
            n.parentNode.removeChild(n);
          });
        });
        setField(form, 'country_code', code);
        if (locale) setField(form, 'locale_code', locale);
        // stay on this page (the footer form's own return_to is the home page)
        setField(form, 'return_to', window.location.pathname);
        form.submit();
        return;
      }
      // no localization form on the page (rare): build the one the Liquid tag would
      var built = document.createElement('form');
      built.method = 'POST';
      built.action = '/localization';
      built.acceptCharset = 'UTF-8';
      [
        { name: 'form_type', value: 'localization' },
        { name: 'utf8', value: '\u2713' },
        { name: '_method', value: 'put' },
        { name: 'country_code', value: code },
        { name: 'return_to', value: window.location.pathname }
      ].concat(locale ? [{ name: 'locale_code', value: locale }] : []).forEach(function (f) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = f.name;
        input.value = f.value;
        built.appendChild(input);
      });
      document.body.appendChild(built);
      built.submit();
    }

    // set a field on the form: the footer's country_code is a <select>, the rest are inputs
    function setField(form, name, value) {
      var field = form.querySelector('[name="' + name + '"]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        form.appendChild(field);
      }
      field.value = value;
    }

    // Nothing is sent until Apply: choosing a country only updates the language row. The
    // notes (desktop popover, mobile sheet) carry the helper line, and "Updating..." while the
    // page reloads.
    var locNotes = toArray(root.querySelectorAll('[data-bh-loc-note]'));
    var locNoteText = locNotes.length ? locNotes[0].textContent : '';
    var textLocBusy = root.getAttribute('data-bh-s-loc-busy') || '';

    // the language the page is in, as the server rendered it (a select in a multi-language
    // market, the single language otherwise)
    (function () {
      var first = locLangSelects[0];
      if (first && !first.hidden) {
        locServerLangs = toArray(first.options).map(function (o) { return o.value; });
        var cur = toArray(first.options).filter(function (o) { return o.defaultSelected; })[0];
        if (cur) locCurrentLang = cur.value.toLowerCase();
      } else if (locCurrentLang) {
        locServerLangs = [locCurrentLang];
      }
    })();

    function setLocNote(text) {
      locNotes.forEach(function (n) { n.textContent = text; });
    }

    function setLocBusy(on) {
      locBusy = on;
      locSelects.concat(locLangSelects, locApplies).forEach(function (el) {
        el.disabled = on;
        if (on) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
      });
    }

    // back to the normal note, controls usable again (also the pageshow / reopen path)
    function resetLocNote() {
      setLocNote(locNoteText);
      setLocBusy(false);
    }

    // Apply: the chosen country, and the chosen language when a language select is showing.
    // Nothing changed = just close. This is the one submit path (the footer's localization
    // form, see submitCountry).
    function applyLoc() {
      if (locBusy) return;
      var layer = activeLayer();
      var sel = layerPart(layer, '[data-bh-loc-select]');
      var langSel = layerPart(layer, '[data-bh-loc-lang-select]');
      var code = sel ? sel.value : '';
      if (code && !langsFor(code)) {
        // the languages for this country are not known yet: wait for them, show the row, and let
        // the visitor press Apply again rather than submit without a language choice
        loadLocMarkets().then(function () { if (locOpen) renderLang(code); });
        return;
      }
      var lang = langSel && !langSel.hidden ? langSel.value : '';
      var sameCountry = !code || code === locInitial;
      var sameLang = !lang || lang.toLowerCase() === locCurrentLang;
      if (sameCountry && sameLang) {
        closeLoc(true);
        return;
      }
      // a small busy state while the page reloads; the controls cannot be changed again
      setLocNote(textLocBusy);
      setLocBusy(true);
      submitCountry(code || locInitial, lang || null);
    }

    locSelects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        locSelects.forEach(function (other) { if (other !== sel) other.value = sel.value; });
        renderLang(sel.value);
      });
    });
    locApplies.forEach(function (btn) {
      btn.addEventListener('click', applyLoc);
    });

    locTriggers.forEach(function (t) {
      t.addEventListener('click', function () { openLoc(t, null, null); });
      // fetch the markets file ahead of the click so the language row is right at once
      t.addEventListener('mouseenter', loadLocMarkets);
      t.addEventListener('focus', loadLocMarkets);
    });
    toArray(root.querySelectorAll('[data-bh-loc-close], [data-bh-loc-overlay]')).forEach(function (b) {
      b.addEventListener('click', function () { closeLoc(true); });
    });
    api.openLoc = function () { openLoc(null, null, null); };
    api.applyLoc = function (code, locale) { submitCountry(code, locale); return true; };

    // From the other country / language controls (BrittenHeaderLoc.open): desktop only, and
    // only with the header pill on the page. If the pill is scrolled out of sight (or hidden in
    // the compact bar) the page goes back to the top first, so the panel opens under it.
    api.openLocFor = function (code) {
      var pill = root.querySelector('.bh-locwrap [data-bh-loc-open]');
      if (!pill || !locPop || !isDesktop()) return false;
      var origin = document.activeElement;
      if (!origin || origin === document.body || root.contains(origin)) origin = null;
      if (locOpen) closeLoc(false);
      var r = pill.getBoundingClientRect();
      var inView = isShown(pill) && r.bottom > 0 && r.top < window.innerHeight &&
        window.getComputedStyle(pill).visibility !== 'hidden';
      if (inView) {
        openLoc(pill, code, origin);
        locOpenedAt = Date.now();
        return true;
      }
      try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch (err) { window.scrollTo(0, 0); }
      // two frames: the compact bar hands back to the editorial header in the first
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          openLoc(pill, code, origin);
          locOpenedAt = Date.now();
        });
      });
      return true;
    };

    /* ------------------------------ mobile menu ------------------------- */
    // "Ask the workshop" in the new mobile menu: open = 09:00 to 16:00 Europe/London, Monday to Friday, not on a
    // closed date (the same rule as the footer card, assets/britten-footer.js; its closed dates are in
    // data-bf-closed there). The words come from the row's data-bh-ws-* attributes. Without Intl time zone support
    // (or without the attributes) the row keeps the server's neutral line and no dot.
    function updateWorkshop() {
      if (!workshops.length || typeof Intl === 'undefined' || !Intl.DateTimeFormat) return;
      var p = {};
      try {
        new Intl.DateTimeFormat('en-GB', { timeZone: WS_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
          .formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      } catch (err) { return; }
      var midnight = Date.UTC(+p.year, +p.month - 1, +p.day);
      var minutes = ((+p.hour) % 24) * 60 + (+p.minute);
      if (isNaN(midnight) || isNaN(minutes)) return;
      var card = document.querySelector('[data-bf-closed]');
      var closed = ((card && card.getAttribute('data-bf-closed')) || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
      // is the workshop working on the Bath day i days from today? (a weekday that is not a closed date)
      function working(i) {
        var d = new Date(midnight + i * 864e5);
        var dow = d.getUTCDay();
        return dow >= 1 && dow <= 5 && closed.indexOf(d.toISOString().slice(0, 10)) === -1;
      }
      var isOpen = working(0) && minutes >= WS_FROM && minutes < WS_TO;
      workshops.forEach(function (el) {
        var out = el.querySelector('[data-bh-ws-text]');
        var text = '';
        if (isOpen) {
          text = el.getAttribute('data-bh-ws-open') || '';
        } else if (working(0) && minutes < WS_FROM) {
          text = el.getAttribute('data-bh-ws-today') || '';
        } else {
          var k = 1;
          while (k < 15 && !working(k)) k++;
          var when = el.getAttribute('data-bh-ws-tomorrow') || '';
          if (k > 1) {
            var day = '';
            try {
              day = new Intl.DateTimeFormat(el.getAttribute('data-bh-ws-locale') || undefined, { weekday: 'long', timeZone: 'UTC' }).format(midnight + k * 864e5);
            } catch (err) { day = ''; }
            when = day ? (el.getAttribute('data-bh-ws-on') || '').replace('{day}', day) : '';
          }
          text = when ? (el.getAttribute('data-bh-ws-next') || '').replace('{when}', when) : '';
        }
        if (!out || !text) return;   // not enough to say: leave the neutral line
        out.textContent = text;
        el.setAttribute('data-bh-ws-state', isOpen ? 'open' : 'closed');
      });
    }

    /* Modal semantics while the drawer is open (phones). makeModal / unmakeModal are a pair: makeModal records
       every attribute it touches, unmakeModal puts all of them back, and every close path goes through closeMenu
       (or closeAll, which calls it), so no path can forget. Both are safe to call twice. */
    var modalLayer = null;   // { label: the nav's original aria-label, saved: [{ el, aria }] } while the modal is on
    var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, LINK: 1, META: 1, TEMPLATE: 1, NOSCRIPT: 1 };

    // a visible fixed layer sitting at drawer height or above (the bag drawer): a live overlay, not page
    function isLiveOverlay(el) {
      var cs = window.getComputedStyle(el);
      return cs.position === 'fixed' && cs.visibility !== 'hidden' && cs.display !== 'none' && (parseInt(cs.zIndex, 10) || 0) >= 1000;
    }

    function makeModal() {
      if (modalLayer) return;
      modalLayer = { label: nav.getAttribute('aria-label'), saved: [] };
      nav.setAttribute('role', 'dialog');
      nav.setAttribute('aria-modal', 'true');
      var name = menuBtn && menuBtn.getAttribute('aria-label');
      if (name) nav.setAttribute('aria-label', name);
      // the list keeps a navigation landmark, named as the nav was
      if (scrollBox) {
        scrollBox.setAttribute('role', 'navigation');
        if (modalLayer.label) scrollBox.setAttribute('aria-label', modalLayer.label);
      }
      // walk from the nav up to <body>; every sibling off that path is the rest of the page
      var active = document.activeElement;
      for (var node = nav; node && node !== document.body && node.parentNode; node = node.parentNode) {
        toArray(node.parentNode.children).forEach(function (sib) {
          if (sib === node || SKIP_TAGS[sib.tagName] || sib.hasAttribute('inert')) return;
          if (searchWrap && (sib === searchWrap || sib.contains(searchWrap))) return;   // search opens from inside the menu
          if (active && sib.contains(active)) return;
          if (sib.parentNode === document.body && isLiveOverlay(sib)) return;
          modalLayer.saved.push({ el: sib, aria: sib.getAttribute('aria-hidden') });
          sib.setAttribute('inert', '');
          sib.setAttribute('aria-hidden', 'true');   // fallback for WebKit without inert
        });
      }
    }

    function unmakeModal() {
      if (!modalLayer) return;
      modalLayer.saved.forEach(function (r) {
        r.el.removeAttribute('inert');
        if (r.aria === null) r.el.removeAttribute('aria-hidden');
        else r.el.setAttribute('aria-hidden', r.aria);
      });
      nav.removeAttribute('role');
      nav.removeAttribute('aria-modal');
      if (modalLayer.label === null) nav.removeAttribute('aria-label');
      else nav.setAttribute('aria-label', modalLayer.label);
      if (scrollBox) {
        scrollBox.removeAttribute('role');
        scrollBox.removeAttribute('aria-label');
      }
      modalLayer = null;
    }

    /* Back closes the drawer. Opening pushes ONE entry ({bhMenu:true}) unless the current entry already is one.
       X and Esc call history.back() and let popstate do the closing; every other close path (a link, the bag,
       the search button, the breakpoint) closes directly and then drops the entry. The country sheet and search layer
       never touch history. */
    var closingBack = '';   // the method name while a history.back() close is on its way
    function hasMenuEntry() {
      try { return !!(history.state && history.state.bhMenu); } catch (e) { return false; }
    }
    function pushMenuEntry() {
      try { if (!hasMenuEntry()) history.pushState({ bhMenu: true }, ''); } catch (e) { /* no history API: Back just leaves the page */ }
    }
    // replace: clear the marker in place (bfcache restore, a stale entry); otherwise step back off it
    function dropMenuEntry(replace) {
      try {
        if (!hasMenuEntry()) return;
        if (replace) history.replaceState(null, '');
        else history.back();
      } catch (e) { /* nothing to do */ }
    }

    function pageType() {
      var el = root.querySelector('[data-page-type]');
      return (el && el.getAttribute('data-page-type')) || window.location.pathname;
    }

    function openMenu() {
      if (menuOpen || isDesktop()) return;
      menuOpen = true;
      setOpen(-1);
      updateWorkshop();
      syncState();
      makeModal();
      pushMenuEntry();
      requestScrollCheck();
      bhTrack('menu_open', { page_type: pageType() });
      if (scrollBox) scrollBox.scrollTop = 0;
      if (menuClose) menuClose.focus();
    }

    // method: 'x' | 'escape' | 'back' | 'link' | 'search' | 'breakpoint'
    function closeMenu(returnFocus, method) {
      if (!menuOpen) return;
      if (savedOpen) closeSaved(false);
      if (locOpen) closeLoc(false);
      if (searchOpen) closeSearch(false);
      menuOpen = false;
      closingBack = '';
      setOpen(-1);
      unmakeModal();   // before focus moves back: the hamburger is inert while the modal is on
      syncState();
      requestScrollCheck();
      bhTrack('menu_close', { method: method || 'link' });
      if (returnFocus && menuBtn) menuBtn.focus();
    }

    // X and Esc. With our history entry on top, step back and let popstate close; otherwise close now.
    function requestCloseMenu(method) {
      if (!menuOpen || closingBack) return;
      if (!hasMenuEntry()) {
        closeMenu(true, method);
        return;
      }
      closingBack = method;
      try { history.back(); } catch (e) { closeMenu(true, method); return; }
      // popstate should land within a frame or two; if it never does, do not leave her stuck
      setTimeout(function () { if (menuOpen) closeMenu(true, method); }, 400);
    }

    window.addEventListener('popstate', function () {
      if (!menuOpen) {
        // a stale marker (forward into the menu's old entry) is cleared; a popstate after we closed is ignored
        closingBack = '';
        if (hasMenuEntry()) dropMenuEntry(true);
        return;
      }
      if (hasMenuEntry()) return;   // still on the menu's own entry
      closeMenu(true, closingBack || 'back');
    });

    function openSection(i) {
      setOpen(i);
      if (scrollBox) scrollBox.scrollTop = 0;
      if (items[i] && items[i].back) items[i].back.focus();
    }

    function backToRoot() {
      var was = openIdx;
      setOpen(-1);
      if (scrollBox) scrollBox.scrollTop = 0;
      if (was >= 0) items[was].link.focus();
    }

    if (menuBtn) menuBtn.addEventListener('click', openMenu);
    if (menuClose) menuClose.addEventListener('click', function () { requestCloseMenu('x'); });

    // accordion toggles inside the section screens (and the new menu's groups: each opens on its own)
    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-bh-toggle]') : null;
      if (!t || !root.contains(t)) return;
      t.setAttribute('aria-expanded', t.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
    });

    // New mobile menu: the seven sections open one at a time. A tap on the open one closes it (all can be shut).
    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-bh-ms-btn]') : null;
      if (!b || !root.contains(b)) return;
      var wasOpen = b.getAttribute('aria-expanded') === 'true';
      toArray(root.querySelectorAll('[data-bh-ms-btn]')).forEach(function (other) { other.setAttribute('aria-expanded', 'false'); });
      if (!wasOpen) b.setAttribute('aria-expanded', 'true');
      bhTrack('menu_section', { section: (b.getAttribute('aria-controls') || '').replace('bh-ms-', ''), open: !wasOpen });
    });

    // New mobile menu: one delegated listener on the tree reports which link was followed (label, href, section; no personal data)
    var menuTree = root.querySelector('[data-bh-mm]');
    if (menuTree) {
      menuTree.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('a[href]') : null;
        if (!a) return;
        var sec = a.closest('.bh-ms');
        var secBtn = sec && sec.querySelector('[data-bh-ms-btn]');
        var lab = a.querySelector('.bh-ml__t, .bh-mg__guide-t') || a;
        bhTrack('menu_link', {
          label: (lab.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80),
          href: a.getAttribute('href'),
          section: secBtn ? (secBtn.getAttribute('aria-controls') || '').replace('bh-ms-', '') : 'utility'
        });
      });
    }

    // New mobile menu: the panel's Bag link is not the bag (no bh-bag, no data-ajax-cart-trigger: the cart scripts bind
    // to the one in the bar). Close the menu, then press the real bag so the drawer opens as it always does. A
    // modified click (new tab) and a page with no bar bag keep the plain link to the cart.
    if (menuBag) {
      menuBag.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var realBag = root.querySelector('.bh-icons a.bh-bag');
        if (!realBag) return;
        e.preventDefault();
        if (menuOpen) {
          closeMenu(false, 'link');
          dropMenuEntry(false);
        }
        realBag.click();
      });
    }

    /* ------------------- Saved / Your Britten (S1) --------------------------- */
    // Desktop: the panel under the header. Mobile: the menu layer on its account screen.
    // The markup is only there when the flags render it; without it all of this is a no-op.
    // viaKeyboard: the click carried detail 0 (Enter or Space on the trigger, or a script / screen reader)
    function openSaved(trigger, viaKeyboard) {
      var desk = isDesktop();
      var layer = desk ? savedPanel : savedScreen;
      if (!layer || savedOpen) return;
      clearTimer();
      if (locOpen) closeLoc(false);
      if (searchOpen) closeSearch(false);
      setOpen(-1);
      if (!desk && !menuOpen) openMenu();
      savedTrigger = trigger || null;
      savedOpen = true;
      syncState();
      if (!desk && scrollBox) scrollBox.scrollTop = 0;
      var first = layer.querySelector(desk ? '[data-bh-saved-close]' : '[data-bh-saved-back]');
      // Desktop: Close takes focus only for a keyboard open (the trigger shows :focus-visible, or the click had no pointer).
      // A mouse or touch open focuses the panel itself (tabindex -1, no outline), so no ring appears; Esc, Tab and
      // screen readers still start inside it. Mobile: the Back button, always, as before.
      if (desk && !(viaKeyboard || focusVisible(trigger))) first = layer;
      if (first) first.focus();
      requestScrollCheck();
      try {
        var core = window.BrittenCore;
        if (core && core.events) core.events.emit('saved_open', { tool: 'your-britten', count: core.items.count(), surface: 'panel' });
      } catch (err) { /* analytics must never break the header */ }
    }

    function closeSaved(returnFocus) {
      if (!savedOpen) return;
      var desk = isDesktop();
      savedOpen = false;
      syncState();
      requestScrollCheck();
      if (returnFocus) {
        var target = null;
        if (!desk && menuOpen) {
          // the account screen was a second level of the menu: back to the menu list. The new menu has two ways in
          // (the panel's bookmark and the Your Britten row): go back to the one she used.
          if (isM2 && savedTrigger && nav.contains(savedTrigger) && isShown(savedTrigger)) target = savedTrigger;
          else target = (menuRow && isShown(menuRow)) ? menuRow : menuClose;
        } else {
          target = (savedTrigger && isShown(savedTrigger)) ? savedTrigger : savedToggles.filter(isShown)[0];
        }
        if (target) target.focus();
      }
      savedTrigger = null;
    }

    savedToggles.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        if (savedOpen) closeSaved(true);
        else openSaved(btn, e.detail === 0);
      });
    });
    toArray(root.querySelectorAll('[data-bh-saved-close], [data-bh-saved-back]')).forEach(function (btn) {
      btn.addEventListener('click', function () { closeSaved(true); });
    });
    if (savedPanel) {
      // a link in the panel closes it (the page may not change: a #hash on this page)
      savedPanel.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('a[href]') : null;
        if (a && !e.defaultPrevented) closeSaved(false);
      });
    }

    /* ------------------------------ keyboard ---------------------------- */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (savedOpen) {
          closeSaved(true);
        } else if (locOpen) {
          closeLoc(true);
        } else if (searchOpen) {
          // a search field empties itself on Escape in most browsers; the text is kept, so take the key
          e.preventDefault();
          closeSearch(true);
        } else if (menuOpen) {
          requestCloseMenu('escape');
        } else if (openIdx >= 0 && isDesktop()) {
          var was = openIdx;
          closePanelNow();
          focusLink(was);
        }
        return;
      }
      if (e.key === 'Tab' && !isDesktop()) {
        // keep Tab inside the full-screen layer that is open
        var layer = (locOpen && locSheet) ? locSheet.querySelector('.bh-sheet__panel') : (searchOpen ? searchWrap : (menuOpen ? nav : null));
        if (!layer) return;
        var list = focusables(layer);
        if (!list.length) return;
        // Step focus by hand on every Tab. Safari does not Tab to links or buttons by
        // default, so letting the browser move focus lets it jump to a form field behind
        // the layer. Wrap at both ends; focus outside the list goes to the first (Tab)
        // or last (Shift+Tab).
        var at = list.indexOf(document.activeElement);
        var next;
        if (at < 0 || !layer.contains(document.activeElement)) {
          next = e.shiftKey ? list.length - 1 : 0;
        } else if (e.shiftKey) {
          next = (at - 1 + list.length) % list.length;
        } else {
          next = (at + 1) % list.length;
        }
        e.preventDefault();
        list[next].focus();
      }
    });

    /* ---------------------- compact bar (desktop only) ------------------ */
    var compact = false;
    var hidden = false;
    var edge = 0;               // page y of the editorial header's bottom edge
    var lastY = 0;
    var ticking = false;
    var curOffset = 0;

    function scrollY() {
      return window.pageYOffset || docEl.scrollTop || 0;
    }

    function measure() {
      edge = spacer.getBoundingClientRect().bottom + scrollY();
    }

    function setOffset(px) {
      if (px === curOffset) return;
      curOffset = px;
      docEl.style.setProperty('--bh-offset', px + 'px');
    }

    // does the compact bar carry the picker trigger? (settings.britten_header_compact_currency
    // puts bh--cur on the root; the CSS drops the trigger below 1100px either way)
    function compactHasLoc() {
      return root.classList.contains('bh--cur') && !!window.matchMedia && window.matchMedia('(min-width: 1100px)').matches;
    }

    function anyOpen() {
      if (openIdx >= 0 || searchOpen || locOpen || savedOpen) return true;
      var a = document.activeElement;
      return !!(a && header.contains(a) && focusVisible(a));
    }

    function applyCompact(nextCompact, nextHidden, open) {
      var shown = nextCompact && (!nextHidden || open);
      if (nextCompact !== compact) {
        compact = nextCompact;
        if (compact) {
          // start hidden with no transition, then let it slide in
          root.classList.add('bh--instant', 'bh--compact');
          void header.offsetHeight;
          root.classList.remove('bh--instant');
        } else {
          root.classList.remove('bh--compact', 'bh--shown');
        }
      }
      hidden = nextHidden;
      if (locOpen) clampLoc();   // the trigger moves between the editorial header and the compact bar
      if (root.classList.contains('bh--shown') !== shown) {
        root.classList.toggle('bh--shown', shown);
      }
      setOffset(shown ? COMPACT_H : 0);
    }

    /* ---------- phone bar (below 1024px, BH_MOBILE_STICKY) ----------
       At the top of the page nothing changes: announcement, then the static 56px bar. Once the page has scrolled
       M_SHOW_AFTER past the header, the bar (not the announcement) goes position:fixed (bh--mstuck) and hides on
       the way down, shows on the way up (bh--mshown), with M_HYST px of hysteresis so it does not flicker. The
       spacer already holds the bar's 56px, so going fixed moves nothing. It goes back to the static bar at the
       bar's own top edge, where the two sit on the same pixels. The CSS ignores the classes while the drawer or
       the search is open. --bh-mobile-bar on <html> is 56px while the bar shows, else 0px. */
    var mStuck = false;
    var mShown = false;
    var mAnchor = 0;         // the scroll position the last show / hide was measured from
    var mBar = 0;

    function setMobileBar(px) {
      if (px === mBar) return;
      mBar = px;
      docEl.style.setProperty('--bh-mobile-bar', px + 'px');
      setOffset(px);   // phones only (this runs below 1024px): the facet bars that read --bh-offset sit under the bar too
    }

    function applyMobile(stuck, shown) {
      if (stuck !== mStuck) {
        // fixed and hidden (or shown) in one frame with no transition, so nothing slides on the way in
        root.classList.add('bh--instant');
        mStuck = stuck;
        root.classList.toggle('bh--mstuck', stuck);
        root.classList.toggle('bh--mshown', stuck && shown);
        void header.offsetHeight;
        root.classList.remove('bh--instant');
      } else {
        root.classList.toggle('bh--mshown', stuck && shown);
      }
      mShown = shown;
      setMobileBar(stuck && shown && !menuOpen && !searchOpen ? M_BAR_H : 0);
    }

    function resetMobileBar() {
      mStuck = false;
      mShown = false;
      root.classList.remove('bh--mstuck', 'bh--mshown');
      setMobileBar(0);
    }

    function updateMobile() {
      var y = scrollY();
      var barTop = edge - spacer.offsetHeight;   // page y of the static bar's top edge
      var stuck = mStuck ? y > barTop : y > edge + M_SHOW_AFTER;
      var a = document.activeElement;
      var keep = menuOpen || searchOpen || !!(a && header.contains(a) && focusVisible(a));
      var shown = mShown;
      if (!stuck || keep) {
        shown = true;
        mAnchor = y;
      } else if (!mStuck) {
        shown = false;     // just went fixed on the way down: starts hidden
        mAnchor = y;
      } else if (shown) {
        if (y < mAnchor) mAnchor = y;
        else if (y - mAnchor > M_HYST) { shown = false; mAnchor = y; }
      } else {
        if (y > mAnchor) mAnchor = y;
        else if (mAnchor - y > M_HYST) { shown = true; mAnchor = y; }
      }
      applyMobile(stuck, shown);
    }

    function update() {
      ticking = false;
      if (!isDesktop()) {
        if (BH_MOBILE_STICKY) updateMobile();
        return;
      }
      var y = scrollY();
      var down = y > lastY + DEAD_BAND;
      var up = y < lastY - DEAD_BAND;
      lastY = y;
      var nextCompact = compact ? y > edge - RETURN_BEFORE : y > edge + SHOW_AFTER;
      // the picker trigger is in the compact bar only when the shop setting is on, and then only
      // from 1100px up (class bh--cur): the popover stays open with it. Otherwise the compact
      // bar has no trigger, so a popover open from the editorial header closes when the
      // compact bar takes over
      if (locOpen && nextCompact && !compact && !compactHasLoc()) closeLoc(false);
      // (an open search sheet stays open when the compact bar takes over: the sheet's own input holds the text and the
      // sheet hangs from whichever bar is showing)
      var open = anyOpen();
      var nextHidden = hidden;
      if (!nextCompact || y < edge + HIDE_AFTER || open) {
        nextHidden = false;
      } else if (down) {
        nextHidden = true;
      } else if (up) {
        nextHidden = false;
      }
      applyCompact(nextCompact, nextHidden, open);
    }

    function requestScrollCheck() {
      // the desktop compact bar; on phones only the sticky bar needs it (BH_MOBILE_STICKY)
      if (ticking || (!isDesktop() && !BH_MOBILE_STICKY)) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestScrollCheck, { passive: true });
    // a keyboard user tabbing into the bar brings it back
    header.addEventListener('focusin', function () { if (!isDesktop()) requestScrollCheck(); });

    var resizeQueued = false;
    window.addEventListener('resize', function () {
      if (resizeQueued) return;
      resizeQueued = true;
      window.requestAnimationFrame(function () {
        resizeQueued = false;
        // a picker whose trigger has just gone away (compact bar below 1100px)
        if (locOpen && locTrigger && !isShown(locTrigger)) closeLoc(false);
        clampLoc();
        measure();
        requestScrollCheck();
      });
    }, { passive: true });
    window.addEventListener('load', function () {
      measure();
      requestScrollCheck();
    });

    function closeAll(method) {
      clearTimer();
      if (menuOpen) closeMenu(false, method || 'breakpoint');   // also puts the modal attributes back
      unmakeModal();
      if (locOpen) closeLoc(false);
      if (searchOpen) closeSearch(false);
      if (savedOpen) closeSaved(false);
      menuOpen = false;
      setOpen(-1);
      syncState();
    }

    // Back/forward can restore this page from the browser's cache exactly as it was
    // left, menu open and scroll locked. Start clean.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      closeAll('link');
      dropMenuEntry(true);   // restored with the menu's history marker: clear it, reopen nothing
      resetMobileBar();
      measure();
      requestScrollCheck();
    });

    var wasDesktop = isDesktop();
    function onBreakpoint() {
      // some engines fire "change" without a real change (e.g. during a screenshot)
      if (isDesktop() === wasDesktop) return;
      wasDesktop = isDesktop();
      closeAll('breakpoint');
      dropMenuEntry(false);
      resetMobileBar();
      compact = false;
      hidden = false;
      root.classList.remove('bh--compact', 'bh--shown', 'bh--instant');
      setOffset(0);
      setPlaceholder();
      syncSearchRole();
      measure();
      requestScrollCheck();
    }
    if (mqDesktop) {
      if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onBreakpoint);
      else if (mqDesktop.addListener) mqDesktop.addListener(onBreakpoint);
    }

    /* ---------------------------- announcements ------------------------- */
    function initAnnouncements() {
      if (!ann) return;
      var region = ann.querySelector('[data-bh-ann-region]');
      var track = ann.querySelector('[data-bh-ann-track]');
      var pauseBtn = ann.querySelector('[data-bh-ann-toggle]');
      if (!region || !track) return;

      var messages = toArray(track.children).filter(function (el) { return el.tagName === 'DIV'; });
      if (!messages.length) {
        if (pauseBtn) pauseBtn.hidden = true;
        return;
      }

      var index = 0;
      var paused = false;
      var held = false;
      var textPause = root.getAttribute('data-bh-s-pause') || '';
      var textPlay = root.getAttribute('data-bh-s-play') || '';

      // Which message is current: only it is reachable by keyboard or screen reader.
      function setCurrent(i) {
        index = i;
        messages.forEach(function (m, k) {
          var on = k === i;
          if (on) m.removeAttribute('aria-hidden');
          else m.setAttribute('aria-hidden', 'true');
          toArray(m.querySelectorAll('a')).forEach(function (a) {
            if (on) a.removeAttribute('tabindex');
            else a.setAttribute('tabindex', '-1');
          });
        });
      }

      function show(i) {
        setCurrent(i);
        track.style.setProperty('--bh-i', String(i));
      }
      show(0);

      // GA4: same event, same parameters as the old bar
      region.addEventListener('click', function (e) {
        var msg = e.target.closest ? e.target.closest('[data-message-id]') : null;
        if (!msg) return;
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'announcement_bar_click', {
            message_id: msg.getAttribute('data-message-id'),
            page_type: region.getAttribute('data-page-type') || 'unknown'
          });
        }
      });

      if (messages.length < 2) {
        if (pauseBtn) pauseBtn.hidden = true;
        return;
      }

      region.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') held = true; });
      region.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') held = false; });
      region.addEventListener('focusin', function () { held = true; });
      region.addEventListener('focusout', function () { held = false; });

      if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
          paused = !paused;
          ann.classList.toggle('bh-ann--paused', paused);
          pauseBtn.setAttribute('aria-label', paused ? textPlay : textPause);
        });
      }

      // Last -> first must not slide backwards through every message. So a copy of the
      // first message sits after the last one: the track slides ON to the copy, then
      // snaps back to the real first message with no transition. The copy is hidden
      // from screen readers, not focusable, and carries no data-message-id (so a click
      // on it during the slide sends no GA4 event). With reduced motion there is no
      // slide at all, so no copy is needed.
      var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var clone = null;
      var snapTimer = null;
      if (!reduce) {
        clone = messages[0].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-bh-clone', '');
        clone.removeAttribute('data-message-id');
        clone.removeAttribute('data-static');
        toArray(clone.querySelectorAll('[id]')).forEach(function (n) { n.removeAttribute('id'); });
        toArray(clone.querySelectorAll('[data-message-id]')).forEach(function (n) { n.removeAttribute('data-message-id'); });
        toArray(clone.querySelectorAll('style, script')).forEach(function (n) { n.parentNode.removeChild(n); });
        toArray(clone.querySelectorAll('a')).forEach(function (a) { a.setAttribute('tabindex', '-1'); });
        track.appendChild(clone);
      }

      function snapBack() {
        snapTimer = null;
        track.classList.add('bh-ann__track--snap');
        track.style.setProperty('--bh-i', '0');
        void track.offsetHeight;
        track.classList.remove('bh-ann__track--snap');
      }

      function advance() {
        if (index + 1 < messages.length) {
          show(index + 1);
          return;
        }
        if (!clone) {
          show(0);
          return;
        }
        setCurrent(0);
        track.style.setProperty('--bh-i', String(messages.length));
        clearTimeout(snapTimer);
        snapTimer = setTimeout(snapBack, ANNOUNCE_SLIDE_MS + 60);
      }

      // A timeout chain, not an interval, so slide 1 can dwell longer. While paused, held
      // or hidden the tick does not advance; it just waits again for the current slide's dwell.
      function dwell() {
        return index === 0 ? ANNOUNCE_FIRST_MS : ANNOUNCE_MS;
      }
      function tick() {
        if (!(paused || held || document.hidden)) advance();
        setTimeout(tick, dwell());
      }
      setTimeout(tick, dwell());
    }

    /* -------------------------------- go -------------------------------- */
    setPlaceholder();
    syncSearchRole();
    syncState();
    dropMenuEntry(true);   // a reload can bring back the menu's history marker with the menu shut
    initAnnouncements();
    measure();
    lastY = scrollY();
    requestScrollCheck();
    log('ready');
  }

  window._brittenHeaderInit = init;

  // The header is a section, so the theme customiser re-renders it (and drops every
  // listener with the old markup) when a Popular search block is edited. Set the new
  // markup up again; init() skips a root that is already set up.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-bh-header]')) init();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
